import { memo, useCallback, useState, useEffect, useRef, useMemo, type Dispatch, type SetStateAction } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "../components/ui/button";
import { Mic, Send, Loader2, FileText, FileCheck, ArrowLeft, MessageSquare } from "lucide-react";
import { ConversationEntry, ResponseResult, Avatar, DialogState, PatientInterviewState, UserProfile } from "../types";
import { AuthService } from "../lib/auth";
import { questionnaireEmptyMessage } from "../lib/interview-document-status";
import {
  isExperimentDirectEntryUser,
  readRememberedFinishedInterviewId,
  rememberFinishedInterviewId,
  startExperimentDirectInterview,
  startFreshExperimentDirectInterview,
} from "../lib/experiment-direct-entry";
import ConversationLog from "../components/conversation-log";
import { AvatarFaceIcon, LearnerIcon } from "../components/ConversationLogAvatar";
import { getAvatarImage } from "../lib/avatarImages";
import DentistFSMPanel from "../components/DentistFSMPanel";
import PatientInterviewPanel from "../components/PatientInterviewPanel";
import ErrorModal from "../components/error-modal";
import DebugModals from "../components/interview/DebugModals";
import { QuestionnaireDocument, type QuestionnaireJsonData } from "../components/QuestionnaireDocument";
import { InstructionPreviewBody } from "../components/InstructionPreviewBody";
import {
  DockProgressBadge,
  InterviewProgressGauge,
  ReportScoreGauges,
  isOverallReady,
  type ProgressGain,
} from "../components/InterviewProgressGauge";
import {
  applyLivePatientScore,
  computeInterviewProgress,
  computeReportProgress,
  diffInterviewProgress,
  type InterviewProgress,
  type LiveScore,
  type ProgressSourceTask,
} from "../lib/interview-task-progress";
import { InterviewThemeChip, interviewThemeLabels } from "../components/InterviewThemeChip";
import { apiRequest } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";
import {
  CanonicalPoseImageUrls,
  fetchCanonicalPoseImageUrls,
  fetchEyePosition,
  EyePosition,
  InterviewAvatarAssets,
  resolveInterviewAvatarAssets,
} from "../lib/avatarAssets";
import {
  useInterviewWebSocket,
  type CounterpartPresentation,
  type SttCorrectionDraft,
} from "../hooks/useInterviewWebSocket";
import { agentDebugLog, setAgentDebugWsSender } from "../lib/agentDebugLog";
import { useAudioRecording, AudioRecordingOptions } from "../hooks/useAudioRecording";
import { useInterviewLayoutMode } from "../hooks/useInterviewLayoutMode";
import { useBrowserBackGuard } from "../hooks/useBrowserBackGuard";
import { INTERVIEW_FINISHED_STATE_KEY } from "../lib/browserBackGuard";
import { useResponseClipQueue } from "../hooks/useResponseClipQueue";
import { WebSocketClient } from "../lib/websocket";
import { preloadImageUrls } from "../lib/imagePreload";
import { PlaybackIssueDetails, unlockMediaPlayback, requestMediaElementUnlock } from "../lib/mediaPlayback";
import { startAudioSessionKeepAlive, resumeAudioSessionKeepAlive, stopAudioSessionKeepAlive } from "../lib/audioSessionKeepAlive";
import VisemePlayer, { EyeMotionConfig, JawMotionConfig, MappedVisemeEvent, VisemeRenderMode } from '../components/viseme-player';
import AvatarIdle from '../components/avatar-idle';
import AvatarCanvasIdle from '../components/avatar-canvas-idle';
import UnifiedVideoPlayer from '../components/unified-video-player';
import { toUserFacingError } from '../lib/user-facing-error';

const PATIENT_JAW_MOTION = {
  transformOrigin: "50% 61%",
  maxTranslateYPercent: 1.18,
  maxScaleYDelta: 0.012,
  maxScaleXDelta: 0,
  transitionMs: 55,
} as const;

const DENTIST_JAW_MOTION = {
  transformOrigin: "50% 62%",
  maxTranslateYPercent: 2.8,
  maxScaleYDelta: 0,
  maxScaleXDelta: 0,
  transitionMs: 55,
} as const;

const MALE_DENTIST_FRONTBASE_JAW_MOTION = {
  ...DENTIST_JAW_MOTION,
  maxTranslateYPercent: 0.85,
  canvasMouthPatch: {
    x: 0.395,
    y: 0.525,
    width: 0.21,
    height: 0.145,
    maskCenterYRatio: 0.46,
    maskRadiusXRatio: 0.38,
    maskRadiusYRatio: 0.28,
    featherRatio: 0.035,
  },
} as const;

const FEMALE_20_34_BASE_AVATAR_OFFSET_X_PX = 20;
const MOBILE_VIDEO_AVATAR_CENTERING_OFFSET_X_PX: Record<string, number> = {
  male_20_34: 0,
  male_35_49: 0,
  male_50_plus: 0,
  female_20_34: -15,
  female_35_49: 0,
  female_50_plus: 0,
  male_default: 0,
  female_default: 0,
};
const AVATAR_CANVAS_WIDTH = 960;
const AVATAR_CANVAS_HEIGHT = 540;
type AvatarPlaybackMode = "video" | VisemeRenderMode;
type MobilePanelView = "main" | "log";
type InterviewActivityStatus = "preparing" | "recording" | "stt" | "patient";
const AVATAR_PLAYBACK_MODE_STORAGE_KEY = "interviewAvatarPlaybackMode";

/**
 * ブラウザの F5 リロードを検出する。
 *
 * 旧実装の欠陥: performance.getEntriesByType("navigation")[0] は SPA ナビゲーション
 * では更新されないため、/admin で F5 した後に React Router で /interview へ遷移しても
 * "reload" が残っていた（偽陽性）。
 *
 * 修正: navigation エントリの URL パスが現在の pathname と一致する場合のみ reload と判定。
 * SPA 遷移後は nav.name が遷移元ページのままになるので偽陽性が排除される。
 */
function isBrowserReloadNavigation(): boolean {
  if (typeof window === "undefined" || typeof performance === "undefined") return false;
  try {
    const nav = performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined;
    if (!nav) {
      // Legacy API fallback (Safari < 15 等)
      const legacyPerf = performance as Performance & { navigation?: { type?: number } };
      return legacyPerf.navigation?.type === 1;
    }
    if (nav.type !== "reload") return false;
    // SPA ナビゲーション後の偽陽性を防ぐため、エントリの URL パスと現在のパスを比較する。
    const navPathname = new URL(nav.name, window.location.href).pathname;
    return navPathname === window.location.pathname;
  } catch {
    return false;
  }
}

/** reloadSessionState がこれ以上 pending/refreshing に留まれる最大時間 (ms) */
const RELOAD_SESSION_TIMEOUT_MS = 8000;

type TerminalInterviewStatus = "finishing" | "scored" | "score_failed";

const TERMINAL_INTERVIEW_STATUSES = new Set<TerminalInterviewStatus>([
  "finishing",
  "scored",
  "score_failed",
]);

function isTerminalInterviewStatus(status: unknown): status is TerminalInterviewStatus {
  return typeof status === "string" && TERMINAL_INTERVIEW_STATUSES.has(status as TerminalInterviewStatus);
}

function getTerminalInterviewStatusCopy(status: TerminalInterviewStatus): {
  title: string;
  description: string;
} {
  if (status === "finishing") {
    return {
      title: "採点中の面接です",
      description: "この面接はすでに採点を開始しています。評価ページで結果を確認してください。",
    };
  }
  if (status === "score_failed") {
    return {
      title: "採点状況を確認してください",
      description: "この面接は採点処理が完了していません。評価ページに戻って状態を確認してください。",
    };
  }
  return {
    title: "採点済みの面接です",
    description: "この面接は採点済みです。続きの発話は受け付けません。",
  };
}

type FinishInterviewResponse = {
  success?: boolean;
  status?: "accepted" | string;
  jobId?: string;
  interviewId?: number | null;
};

type ScoringJobStatusResponse = {
  jobId: string;
  status: "pending" | "running" | "completed" | "failed";
  interviewId?: number | null;
  elapsedMs?: number;
  error?: string;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForScoringCompletion(
  jobId: string,
  onUpdate: (status: ScoringJobStatusResponse) => void,
): Promise<ScoringJobStatusResponse> {
  const startedAt = Date.now();
  const timeoutMs = 15 * 60 * 1000;
  const intervalMs = 2500;

  while (Date.now() - startedAt < timeoutMs) {
    const res = await fetch(`/api/scoring-status/${encodeURIComponent(jobId)}`, {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`採点状況の取得に失敗しました: ${res.status} ${text}`);
    }

    const status = await res.json() as ScoringJobStatusResponse;
    onUpdate(status);
    if (status.status === "completed") return status;
    if (status.status === "failed") {
      throw new Error(status.error || "採点ジョブが失敗しました");
    }
    await sleep(intervalMs);
  }

  throw new Error("採点が時間内に完了しませんでした。しばらくしてからマイページで結果を確認してください。");
}

const DEFAULT_DENTIST_EYE_POSITION: EyePosition = {
  top: "39.4%",
  left: "41.8%",
  width: "15.55%",
  height: "7.5%",
};

function shiftPercent(value: string, delta: number): string {
  if (!value.trim().endsWith("%")) return value;
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return value;
  return `${numeric + delta}%`;
}

function buildSpeakingEyeMotion(
  eyePosition: EyePosition,
  eyeMaskUrl?: string,
  canvasBlinkMaskUrl?: string
): EyeMotionConfig {
  return {
    maskImage: eyeMaskUrl ? `url(${eyeMaskUrl})` : undefined,
    maskMode: eyeMaskUrl ? "luminance" : undefined,
    canvasBlinkMaskUrl,
    maskPosition: eyeMaskUrl ? "center" : `${shiftPercent(eyePosition.left, -4)} ${shiftPercent(eyePosition.top, -1)}`,
    maskSize: eyeMaskUrl ? "100% 100%" : `${shiftPercent(eyePosition.width, 8)} ${shiftPercent(eyePosition.height, 2)}`,
    maxOffsetXPercent: 0.05,
    maxOffsetYPercent: 0.03,
    firstBlinkMinMs: 750,
    firstBlinkMaxMs: 1350,
    intervalMinMs: 2600,
    intervalMaxMs: 4800,
    transitionMs: 22,
    centerReturnChance: 0.62,
  };
}

function isFemale2034Avatar(avatar: Avatar | undefined, assets: InterviewAvatarAssets): boolean {
  if (!avatar || avatar.type === 'dentist') return false;

  const gender = avatar.gender?.toLowerCase();
  const age = avatar.age;
  const assetPath = [
    assets.runtimePackRoot,
    assets.baseImageUrl,
    avatar.videoFiles?.runtimePath,
  ].join(" ");

  return (
    assetPath.includes("female_20_34") ||
    (gender === "female" && typeof age === "number" && age >= 20 && age <= 34)
  );
}

function resolveAvatarAssetProfileKey(
  avatar: Avatar | undefined,
  assets: InterviewAvatarAssets
): string | null {
  const explicitProfileKey = avatar?.videoFiles?.assetProfileKey;
  if (explicitProfileKey) return explicitProfileKey;

  const assetPathSegments = [
    assets.runtimePackRoot,
    assets.baseImageUrl,
    avatar?.videoFiles?.runtimePath,
    avatar?.videoFiles?.baseVideo,
    avatar?.videoFiles?.dubbingVideo,
    avatar?.videoFiles?.image,
  ].filter(Boolean).join(" ").split(/[\s/]+/);

  return Object.keys(MOBILE_VIDEO_AVATAR_CENTERING_OFFSET_X_PX)
    .find((profileKey) => assetPathSegments.includes(profileKey)) ?? null;
}

function getMobileVideoAvatarCenteringOffsetX(
  avatar: Avatar | undefined,
  assets: InterviewAvatarAssets
): number {
  const profileKey = resolveAvatarAssetProfileKey(avatar, assets);
  return profileKey ? MOBILE_VIDEO_AVATAR_CENTERING_OFFSET_X_PX[profileKey] ?? 0 : 0;
}

function isMaleDentistFrontbaseAvatar(avatar: Avatar | undefined, assets: InterviewAvatarAssets): boolean {
  return (
    avatar?.type === "dentist" &&
    assets.runtimePackRoot.includes("/dentists/male_default/runtime_frontbase_champion_")
  );
}

function isAvatarPlaybackMode(value: string | null): value is AvatarPlaybackMode {
  return value === "video" || value === "canvas" || value === "dom";
}

function resolveAvatarPlaybackMode(): AvatarPlaybackMode {
  if (typeof window === "undefined") return "video";
  const params = new URLSearchParams(window.location.search);
  const queryMode = params.get("avatarMode") ?? params.get("avatarRenderer");
  if (isAvatarPlaybackMode(queryMode)) return queryMode;
  try {
    const storedMode = window.localStorage.getItem(AVATAR_PLAYBACK_MODE_STORAGE_KEY);
    if (isAvatarPlaybackMode(storedMode)) return storedMode;
  } catch {
    // localStorage is optional; default below keeps the interview usable.
  }
  return "video";
}

function persistAvatarPlaybackMode(mode: AvatarPlaybackMode) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AVATAR_PLAYBACK_MODE_STORAGE_KEY, mode);
  } catch {
    // Ignore private browsing/storage failures.
  }
}

function resolveVideoUrl(path?: string | null): string | null {
  if (!path) return null;
  if (/^(https?:|blob:|data:)/.test(path)) return path;
  if (path.startsWith("/videos/")) return path;
  if (path.startsWith("videos/")) return `/${path}`;
  return `/videos/${path.replace(/^\/+/, "")}`;
}

interface AvatarStageProps {
  currentAvatar: Avatar | undefined;
  assets: InterviewAvatarAssets;
  currentVideoUrl: string | null;
  currentVisemes: MappedVisemeEvent[] | null;
  canonicalPoseImageUrls: CanonicalPoseImageUrls | undefined;
  isSpeakingWithVisemes: boolean;
  disableIdleBlink: boolean;
  patientEyePosition: EyePosition | undefined;
  dentistEyePosition: EyePosition | undefined;
  visemeDebugEnabled: boolean;
  avatarPlaybackMode: AvatarPlaybackMode;
  avatarRenderer: VisemeRenderMode;
  isPlayingResponse: boolean;
  jawMotionConfig: JawMotionConfig;
  visemePlaybackTuning: {
    visualLeadMs: number;
    initialPlaybackDelayMs: number;
  };
  frameFit?: "contain" | "cover";
  contentTransform?: string;
  contentTransformOrigin?: string;
  onVideoEnded: () => void;
  onPlaybackIssue: (issue: PlaybackIssueDetails) => void;
  clipBlobSrc?: string | null;
  clipAudioBuffer?: ArrayBuffer | null;
  onClipEnded?: () => void;
}

const AvatarStage = memo(function AvatarStage({
  currentAvatar,
  assets,
  currentVideoUrl,
  currentVisemes,
  canonicalPoseImageUrls,
  isSpeakingWithVisemes,
  disableIdleBlink,
  patientEyePosition,
  dentistEyePosition,
  visemeDebugEnabled,
  avatarPlaybackMode,
  avatarRenderer,
  isPlayingResponse,
  jawMotionConfig,
  visemePlaybackTuning,
  frameFit = "contain",
  contentTransform,
  contentTransformOrigin,
  onVideoEnded,
  onPlaybackIssue,
  clipBlobSrc,
  clipAudioBuffer,
  onClipEnded,
}: AvatarStageProps) {
  const resolvedPatientEyePosition = useMemo<EyePosition>(() => patientEyePosition ?? {
    top: "37.8%",
    left: "39.8%",
    width: "16.25%",
    height: "5.4%",
  }, [patientEyePosition]);
  const resolvedDentistEyePosition = useMemo<EyePosition>(
    () => dentistEyePosition ?? DEFAULT_DENTIST_EYE_POSITION,
    [dentistEyePosition]
  );
  const patientSpeakingEyeMotion = useMemo(
    () => buildSpeakingEyeMotion(
      resolvedPatientEyePosition,
      assets.runtimePackRoot.includes("/patients/female_20_34/") ? `${assets.runtimePackRoot}/eye_mask.png` : undefined
    ),
    [assets.runtimePackRoot, resolvedPatientEyePosition]
  );
  const dentistSpeakingEyeMotion = useMemo(
    () => buildSpeakingEyeMotion(
      resolvedDentistEyePosition,
      isMaleDentistFrontbaseAvatar(currentAvatar, assets) ? `${assets.runtimePackRoot}/eye_mask.png` : undefined,
      isMaleDentistFrontbaseAvatar(currentAvatar, assets) ? `${assets.runtimePackRoot}/eye_mask.png` : undefined
    ),
    [assets, currentAvatar, resolvedDentistEyePosition]
  );
  const dentistEyeMaskUrl = isMaleDentistFrontbaseAvatar(currentAvatar, assets)
    ? `${assets.runtimePackRoot}/eye_mask.png`
    : undefined;
  const baseVideoUrl = resolveVideoUrl(currentAvatar?.videoFiles?.baseVideo);
  const speakingVideoUrl = resolveVideoUrl(currentAvatar?.videoFiles?.dubbingVideo);
  const responseVideoUrl = isPlayingResponse ? resolveVideoUrl(currentVideoUrl) : null;
  const isVideoPlaybackMode = avatarPlaybackMode === "video";
  const isCoverFrame = frameFit === "cover";
  const baseAvatarOffsetX = isFemale2034Avatar(currentAvatar, assets)
    ? FEMALE_20_34_BASE_AVATAR_OFFSET_X_PX
    : 0;
  const mobileVideoAvatarCenteringOffsetX = isVideoPlaybackMode && isCoverFrame
    ? getMobileVideoAvatarCenteringOffsetX(currentAvatar, assets)
    : 0;
  const avatarOffsetX = baseAvatarOffsetX + mobileVideoAvatarCenteringOffsetX;
  const stageTransforms = [
    avatarOffsetX ? `translateX(${avatarOffsetX}px)` : null,
    contentTransform,
  ].filter(Boolean).join(" ") || undefined;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isVideoPlaybackMode ? "#000000" : undefined,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          // PC/非カバー: 親幅いっぱいで 16:9 を保ち、高さは親より大きくなれば
          // overflow:hidden で上下を少しだけクロップする (=cover 寄り)。
          // モバイルカバー: 親高さで張り、min-width で横もはみ出させる。
          width: isCoverFrame ? undefined : "100%",
          height: isCoverFrame ? "100%" : undefined,
          minWidth: isCoverFrame ? "100%" : "100%",
          aspectRatio: "16/9",
          position: "relative",
          background: isVideoPlaybackMode ? "#000000" : "#FFFFFF",
          flexShrink: 0,
          transform: stageTransforms,
          transformOrigin: contentTransformOrigin ?? (isCoverFrame ? "50% 48%" : "50% 42%"),
        }}
      >
        {avatarPlaybackMode === "video" ? (
          <UnifiedVideoPlayer
            baseVideoUrl={baseVideoUrl}
            responseVideoUrl={responseVideoUrl}
            speakingVideoUrl={speakingVideoUrl}
            isPlayingResponse={isPlayingResponse}
            onResponseEnded={onVideoEnded}
            onPlaybackIssue={onPlaybackIssue}
            clipBlobSrc={clipBlobSrc}
            clipAudioBuffer={clipAudioBuffer}
            onClipEnded={onClipEnded}
          />
        ) : currentAvatar && currentAvatar.type === 'patient' ? (
          <>
            {avatarRenderer === "canvas" ? (
              <AvatarCanvasIdle
                imageUrl={assets.baseImageUrl}
                eyesHalfBlinkUrl={assets.eyesHalfBlinkUrl}
                eyesBlinkUrl={assets.eyesBlinkUrl}
                suppressBlink={disableIdleBlink || isSpeakingWithVisemes}
                eyePosition={resolvedPatientEyePosition}
                internalWidth={AVATAR_CANVAS_WIDTH}
                internalHeight={AVATAR_CANVAS_HEIGHT}
                alt={currentAvatar.name}
              />
            ) : (
              !isSpeakingWithVisemes && (
                <AvatarIdle
                  imageUrl={assets.baseImageUrl}
                  mouthImageUrl={assets.mouthImageUrl}
                  jawMaskUrl={assets.jawMaskUrl}
                  eyesHalfBlinkUrl={assets.eyesHalfBlinkUrl}
                  eyesBlinkUrl={assets.eyesBlinkUrl}
                  suppressBlink={disableIdleBlink}
                  animateHead={true}
                  eyePosition={resolvedPatientEyePosition}
                  alt={currentAvatar.name}
                />
              )
            )}
            {isSpeakingWithVisemes && currentVideoUrl && currentVisemes && (
              <div className="absolute inset-0 z-10">
                <VisemePlayer
                  audioUrl={currentVideoUrl}
                  visemes={currentVisemes}
                  baseImageUrl={assets.baseImageUrl}
                  mouthImageUrl={assets.mouthImageUrl}
                  visemeImageUrls={assets.visemeImageUrls}
                  canonicalPoseImageUrls={canonicalPoseImageUrls}
                  debug={visemeDebugEnabled}
                  runtimePackRoot={assets.runtimePackRoot}
                  eyeMotion={patientSpeakingEyeMotion}
                  jawMotion={jawMotionConfig}
                  visualProfile={avatarRenderer === "canvas" ? "generatedTransitions" : "endingSettle"}
                  renderMode={avatarRenderer}
                  canvasEyePosition={resolvedPatientEyePosition}
                  canvasInternalWidth={AVATAR_CANVAS_WIDTH}
                  canvasInternalHeight={AVATAR_CANVAS_HEIGHT}
                  visualLeadMs={visemePlaybackTuning.visualLeadMs}
                  initialPlaybackDelayMs={visemePlaybackTuning.initialPlaybackDelayMs}
                  onPlaybackIssue={onPlaybackIssue}
                  onEnded={onVideoEnded}
                />
              </div>
            )}
          </>
        ) : currentAvatar && currentAvatar.type === 'dentist' ? (
          <>
            {avatarRenderer === "canvas" ? (
              <AvatarCanvasIdle
                imageUrl={assets.baseImageUrl}
                eyesHalfBlinkUrl={assets.eyesHalfBlinkUrl}
                eyesBlinkUrl={assets.eyesBlinkUrl}
                eyeMaskUrl={dentistEyeMaskUrl}
                suppressBlink={disableIdleBlink || isSpeakingWithVisemes}
                eyePosition={resolvedDentistEyePosition}
                internalWidth={AVATAR_CANVAS_WIDTH}
                internalHeight={AVATAR_CANVAS_HEIGHT}
                alt={currentAvatar.name}
              />
            ) : (
              !isSpeakingWithVisemes && (
                <AvatarIdle
                  imageUrl={assets.baseImageUrl}
                  mouthImageUrl={assets.mouthImageUrl}
                  jawMaskUrl={assets.jawMaskUrl}
                  eyesHalfBlinkUrl={assets.eyesHalfBlinkUrl}
                  eyesBlinkUrl={assets.eyesBlinkUrl}
                  suppressBlink={disableIdleBlink}
                  animateHead={true}
                  eyePosition={resolvedDentistEyePosition}
                  alt={currentAvatar.name}
                />
              )
            )}
            {isSpeakingWithVisemes && currentVideoUrl && currentVisemes && (
              <div className="absolute inset-0 z-10">
                <VisemePlayer
                  audioUrl={currentVideoUrl}
                  visemes={currentVisemes}
                  baseImageUrl={assets.baseImageUrl}
                  mouthImageUrl={assets.mouthImageUrl}
                  visemeImageUrls={assets.visemeImageUrls}
                  canonicalPoseImageUrls={canonicalPoseImageUrls}
                  debug={visemeDebugEnabled}
                  runtimePackRoot={assets.runtimePackRoot}
                  eyeMotion={dentistSpeakingEyeMotion}
                  jawMotion={jawMotionConfig}
                  visualProfile={avatarRenderer === "canvas" ? "generatedTransitions" : "endingSettle"}
                  renderMode={avatarRenderer}
                  canvasEyePosition={resolvedDentistEyePosition}
                  canvasInternalWidth={AVATAR_CANVAS_WIDTH}
                  canvasInternalHeight={AVATAR_CANVAS_HEIGHT}
                  visualLeadMs={visemePlaybackTuning.visualLeadMs}
                  initialPlaybackDelayMs={visemePlaybackTuning.initialPlaybackDelayMs}
                  onPlaybackIssue={onPlaybackIssue}
                  onEnded={onVideoEnded}
                />
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
});

export default function Interview() {
  const [conversations, setConversations] = useState<ConversationEntry[]>([]);
  const [currentInterimText, setCurrentInterimText] = useState("");
  const [sttCorrectionDraft, setSttCorrectionDraft] = useState<SttCorrectionDraft | null>(null);
  const [isPlayingResponse, setIsPlayingResponse] = useState(false);
  const [isInterviewSessionReady, setIsInterviewSessionReady] = useState(false);
  const [counterpartPresentation, setCounterpartPresentation] = useState<CounterpartPresentation | null>(null);
  const [isAwaitingResponse, setIsAwaitingResponseValue] = useState(false);
  const [awaitingResponseStage, setAwaitingResponseStage] = useState<"stt" | "patient" | null>(null);
  const isAwaitingResponseRef = useRef(false);
  const setIsAwaitingResponse: Dispatch<SetStateAction<boolean>> = useCallback((value) => {
    const awaiting = typeof value === "function" ? value(isAwaitingResponseRef.current) : value;
    isAwaitingResponseRef.current = awaiting;
    setIsAwaitingResponseValue(awaiting);
    setAwaitingResponseStage((current) => awaiting ? (current ?? "stt") : null);
  }, []);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const localUserQuestionRef = useRef<string>("");
  const suppressSttDraftUntilRef = useRef(0);
  const sttCorrectionRequestedRef = useRef(false);
  const lastSubmittedSttRef = useRef<{ rawText: string; submittedAt: number } | null>(null);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [currentVisemes, setCurrentVisemes] = useState<MappedVisemeEvent[] | null>(null);
  const {
    src: clipBlobSrc,
    audioBuffer: clipAudioBuffer,
    enqueueClip: enqueueResponseClipFromQueue,
    onClipEnded: clipQueueOnEnded,
    clearAll: clearClipQueue,
  } = useResponseClipQueue();
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isDentistSystemPromptModalOpen, setIsDentistSystemPromptModalOpen] = useState(false);
  const [dentistSystemPrompt, setDentistSystemPrompt] = useState<string>('');
  const [dentistName, setDentistName] = useState<string>('');
  const [stageView, setStageView] = useState<"questionnaire" | "interview" | "instruction">("interview");
  const [mobilePanelView, setMobilePanelView] = useState<MobilePanelView>("main");
  const [asyncScoringStatus, setAsyncScoringStatus] = useState<ScoringJobStatusResponse | null>(null);
  const [isFinishInterviewLocked, setIsFinishInterviewLocked] = useState(false);
  const finishInterviewLockedRef = useRef(false);
  const [isInterviewStatusChecking, setIsInterviewStatusChecking] = useState(false);
  const [terminalInterviewGuard, setTerminalInterviewGuard] = useState<{
    status: TerminalInterviewStatus;
    interviewId: number | null;
  } | null>(null);
  const visemeDebugQueryRequested = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("visemeDebug") === "1";
  }, []);
  const visemeDebugRequested = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (visemeDebugQueryRequested) return true;
    try {
      return window.localStorage.getItem("visemeDebug") === "1";
    } catch {
      return false;
    }
  }, [visemeDebugQueryRequested]);
  const visemePlaybackTuning = useMemo(() => {
    const defaults = { visualLeadMs: 20, initialPlaybackDelayMs: 0 };
    if (typeof window === "undefined") return defaults;

    const params = new URLSearchParams(window.location.search);
    const visualLeadMs = Number(params.get("visemeLeadMs"));
    const initialPlaybackDelayMs = Number(params.get("visemeStartDelayMs"));

    return {
      visualLeadMs: Number.isFinite(visualLeadMs)
        ? Math.max(0, Math.min(160, visualLeadMs))
        : defaults.visualLeadMs,
      initialPlaybackDelayMs: Number.isFinite(initialPlaybackDelayMs)
        ? Math.max(0, Math.min(300, initialPlaybackDelayMs))
        : defaults.initialPlaybackDelayMs,
    };
  }, []);
  const disableIdleBlink = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("avatarBlink") === "0";
  }, []);
  const [avatarPlaybackMode, setAvatarPlaybackMode] = useState<AvatarPlaybackMode>(() => resolveAvatarPlaybackMode());
  const avatarPlaybackModeRef = useRef<AvatarPlaybackMode>(avatarPlaybackMode);
  useEffect(() => {
    avatarPlaybackModeRef.current = avatarPlaybackMode;
  }, [avatarPlaybackMode]);
  const avatarRenderer: VisemeRenderMode = avatarPlaybackMode === "dom" ? "dom" : "canvas";
  const switchAvatarPlaybackMode = useCallback((mode: AvatarPlaybackMode) => {
    if (avatarPlaybackModeRef.current === mode) return;
    avatarPlaybackModeRef.current = mode;
    persistAvatarPlaybackMode(mode);
    setAvatarPlaybackMode(mode);
    const label = mode === "video" ? "動画ループ" : mode === "canvas" ? "Canvas viseme" : "DOM viseme";
    toast({ title: "アバター表示を切り替えました", description: label });
  }, [toast]);
  const [fsmState, setFsmState] = useState<DialogState | null>(null);
  const [patientState, setPatientState] = useState<PatientInterviewState | null>(null);
  // 聞き取りゲージ用の患者状態（patientState はデバッグ表示中だけ更新される）
  const [progressPatientState, setProgressPatientState] = useState<PatientInterviewState | null>(null);
  const [targetCategories, setTargetCategories] = useState<string[] | undefined>(undefined);
  const [lastTiming, setLastTiming] = useState<Record<string, number> | null>(null);
  const isSpeakingWithVisemes = !!(isPlayingResponse && currentVisemes && currentVisemes.length > 0 && currentVideoUrl);

  // 認証ロール取得（通常は staff のみ。URLで明示されたデバッグ時だけ実証参加者にも表示）
  const authService = AuthService.getInstance();
  const { data: currentUser, isFetched: isCurrentUserFetched } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => authService.getCurrentUser(),
    staleTime: 5 * 60 * 1000,
  });
  const [reloadSessionState, setReloadSessionState] = useState<"pending" | "refreshing" | "ready">(
    () => isBrowserReloadNavigation() ? "pending" : "ready",
  );
  const isStaff = currentUser?.role === 'admin' || currentUser?.role === 'educator';
  const isExperimentParticipant = Boolean(currentUser?.participantCode);
  // デバッグパネルはアカウント本来のロールで許可する。管理者・教員がログイン画面で「学生」を選び、
  // 学生と同じ講座の流れ（学習 → 課題説明 → 面接）で確認できるようにするため。
  const accountIsStaff =
    isStaff || currentUser?.accountRole === 'admin' || currentUser?.accountRole === 'educator';
  const visemeDebugEnabled = accountIsStaff && visemeDebugRequested;
  const showStaffDebugPanel = visemeDebugEnabled;
  const collectWebSocketDebugState = visemeDebugEnabled;
  const interviewLayoutMode = useInterviewLayoutMode();

  // reloadSessionState の初期化処理。
  // 依存配列に reloadSessionState を含めると invalidateQueries → currentUser 再取得 →
  // effect 再実行 → startExperimentDirectInterview の無限ループになるため、
  // reloadSessionStateRef で「既に処理を開始した」かどうかを管理する。
  const reloadSessionStateRef = useRef(reloadSessionState);
  useEffect(() => {
    if (reloadSessionStateRef.current !== "pending") return;
    if (!isCurrentUserFetched) return;

    // 実証実験の直接エントリーユーザー以外はリロード検知後も即 ready にする
    if (!isExperimentDirectEntryUser(currentUser) || !currentUser?.experimentEntryTarget) {
      reloadSessionStateRef.current = "ready";
      setReloadSessionState("ready");
      return;
    }

    // 二重実行防止: pending → refreshing に変えてから非同期処理を始める
    reloadSessionStateRef.current = "refreshing";
    setReloadSessionState("refreshing");

    let cancelled = false;

    // 安全タイムアウト: 何が起きても RELOAD_SESSION_TIMEOUT_MS 以内に ready へ移行させる
    const safetyTimer = window.setTimeout(() => {
      if (!cancelled) {
        console.warn('[Interview] reload session timeout, forcing ready');
        reloadSessionStateRef.current = "ready";
        setReloadSessionState("ready");
      }
    }, RELOAD_SESSION_TIMEOUT_MS);

    setConversations([]);
    setCurrentInterimText("");
    setSttCorrectionDraft(null);
    setIsAwaitingResponse(false);
    setIsPlayingResponse(false);
    setCurrentVideoUrl(null);
    setCurrentVisemes(null);
    queryClient.removeQueries({ queryKey: ["/api/avatar/current"] });
    queryClient.removeQueries({ queryKey: ["/api/current-interview/documents"] });

    startExperimentDirectInterview(currentUser.experimentEntryTarget)
      .then(async () => {
        if (cancelled) return;
        await queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/current-interview/documents"] });
      })
      .catch((error) => {
        console.error("リロード時の実証実験セッション再取得に失敗しました:", error);
        if (!cancelled) {
          toast({
            title: "面接の再準備に失敗しました",
            description: "もう一度リロードするか、ログインから入り直してください。",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          window.clearTimeout(safetyTimer);
          reloadSessionStateRef.current = "ready";
          setReloadSessionState("ready");
        }
      });

    return () => {
      cancelled = true;
      window.clearTimeout(safetyTimer);
    };
  }, [
    currentUser,
    isCurrentUserFetched,
    queryClient,
    setIsAwaitingResponse,
    toast,
  ]);

  const goToQuestionnaire = () => {
    setMobilePanelView("main");
    setStageView("questionnaire");
  };
  const goToInterview = () => {
    setMobilePanelView("main");
    setStageView("interview");
  };
  const goToInstruction = () => {
    setMobilePanelView("main");
    setStageView("instruction");
  };
  const goToConversationLog = () => {
    setStageView("interview");
    setMobilePanelView("log");
  };
  // 面接中にブラウザの戻る操作で医師の説明画面へ戻らないようにする。
  // 課題文・問診票・ログを開いていれば面接表示へ戻し、面接表示なら何もしない。
  useBrowserBackGuard(true, goToInterview);

  // アバター情報をAPIから取得
  const { data: currentAvatar, isLoading: isAvatarLoading, error: avatarError, refetch: refetchAvatar } = useQuery<Avatar>({
    queryKey: ["/api/avatar/current"],
    queryFn: async () => {
      const response = await fetch("/api/avatar/current", {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) {
        const errorText = await response.text();
        let msg = "アバター情報の取得に失敗しました";
        try { msg = JSON.parse(errorText).message || msg; } catch { msg = errorText || msg; }
        console.error("アバター情報取得エラー:", response.status, msg);
        throw new Error(msg);
      }
      const data = await response.json();
      if (visemeDebugEnabled) {
        console.log("アバター情報取得成功:", data);
      }
      return data;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    enabled: reloadSessionState === "ready",
  });
  const currentInterviewId = (currentAvatar as { interviewId?: number | null } | undefined)?.interviewId ?? null;

  // 課題文・問診票データ（面接中の自分の面接から取得）
  const { data: documentsData, isPending: documentsPending, isError: documentsError } = useQuery<{
    questionnaire?: string;
    questionnaireJson?: QuestionnaireJsonData | null;
    instruction?: {
      title?: string;
      summary?: string;
      target_categories?: string[];
      target_category_labels?: Record<string, string>;
      task_descriptions?: Array<{ title: string; description: string }>;
      tasks?: Array<{ no: number; title: string; details?: string[] } & ProgressSourceTask>;
    } | null;
    patient?: { name: string | null; age: number | null; gender: string | null; birthDate?: string | null };
    interview?: {
      id: number | null;
      status: string | null;
      conditionInstructionId?: number | null;
      finishRequestedAt?: string | null;
      scoringJobId?: string | null;
    } | null;
  }>({
    // 毎回のマウントで取り直す: 前回の面接 or 管理画面で見た面接の問診票が残ってしまう問題を回避
    queryKey: ["/api/current-interview/documents", currentInterviewId],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/current-interview/documents?interview_id=${currentInterviewId}`, { credentials: "include", signal });
      if (!res.ok) throw new Error("取得に失敗しました");
      return res.json();
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    enabled: Boolean(currentInterviewId) && reloadSessionState === "ready",
  });

  // 聞き取りゲージ（#23）: 課題文のタスクごとに、聞けた項目を数える
  const interviewProgress = useMemo<InterviewProgress | null>(
    () =>
      computeInterviewProgress(
        progressPatientState,
        documentsData?.instruction?.tasks,
        documentsData?.instruction?.target_categories,
      ),
    [progressPatientState, documentsData?.instruction?.tasks, documentsData?.instruction?.target_categories],
  );
  const [progressGain, setProgressGain] = useState<ProgressGain | null>(null);
  // 採点と同じ計算の点数。患者の状態や会話が更新されるたびに取り直す（サーバーは保存してから通知する）。
  const [liveScoreTick, setLiveScoreTick] = useState(0);
  useEffect(() => {
    setLiveScoreTick((tick) => tick + 1);
  }, [progressPatientState, conversations.length]);
  const { data: liveScore } = useQuery<LiveScore>({
    queryKey: ["/api/current-interview/live-score", currentInterviewId, liveScoreTick],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/current-interview/live-score?interview_id=${currentInterviewId}`, {
        credentials: "include",
        signal,
      });
      if (!res.ok) throw new Error("点数の取得に失敗しました");
      return res.json();
    },
    placeholderData: (previous) => previous,
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    enabled: Boolean(currentInterviewId) && reloadSessionState === "ready",
  });
  const previousProgressRef = useRef<InterviewProgress | null>(null);
  const progressGainKeyRef = useRef(0);
  const progressGainTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const previous = previousProgressRef.current;
    previousProgressRef.current = interviewProgress;
    // 初回表示・再接続の復元では演出しない
    if (!previous || !interviewProgress) return;
    const diff = diffInterviewProgress(previous, interviewProgress);
    if (diff.gained <= 0) return;
    progressGainKeyRef.current += 1;
    const label = !diff.gainedTask
      ? "聞き取り"
      : diff.gainedTask.no === 0
        ? diff.gainedTask.title
        : `タスク${diff.gainedTask.no}「${diff.gainedTask.title}」`;
    setProgressGain({ key: progressGainKeyRef.current, amount: diff.gained, label });
    if (progressGainTimerRef.current) window.clearTimeout(progressGainTimerRef.current);
    progressGainTimerRef.current = window.setTimeout(() => {
      progressGainTimerRef.current = null;
      setProgressGain(null);
    }, 1400);
  }, [interviewProgress]);
  useEffect(() => () => {
    if (progressGainTimerRef.current) window.clearTimeout(progressGainTimerRef.current);
  }, []);

  // 共有Ref（両フックで使う）
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const recognitionClientIdRef = useRef<string | null>(null);
  const sttSessionMessageIdRef = useRef<string | null>(null);
  const sttPreviousInterimTextRef = useRef<string>('');
  const sttConfirmedTextRef = useRef<string>('');
  // G(STT readyゲート): 既定 true（通常ターンは即送信）。再接続直後など STT 未確立中だけ false にする。
  const sttReadyRef = useRef<boolean>(true);
  const patientCounterpartRole = counterpartPresentation?.role || "患者";
  const patientCounterpartName = counterpartPresentation?.displayName || null;
  const patientWaitingLabel = patientCounterpartName
    ? `${patientCounterpartName}が考え中`
    : `${patientCounterpartRole}が考え中`;
  const counterpartWaitingLabel = currentAvatar?.type === 'dentist'
    ? '歯科医師が返答を生成中'
    : patientWaitingLabel;
  const responseWaitingTitle = currentAvatar?.type === 'dentist'
    ? '歯科医師が返答を生成しています'
    : '患者が回答を考えています';
  const resolvedInterviewId = currentInterviewId ?? documentsData?.interview?.id ?? null;
  const themeLabels = interviewThemeLabels(
    documentsData?.instruction?.target_categories,
    documentsData?.instruction?.target_category_labels,
  );
  const rawInterviewStatus = currentAvatar?.interviewStatus ?? documentsData?.interview?.status ?? null;
  const rememberedFinishedInterviewId = readRememberedFinishedInterviewId();
  const wasFinishedInThisTab =
    typeof resolvedInterviewId === "number" && rememberedFinishedInterviewId === resolvedInterviewId;
  const terminalInterviewStatus = isTerminalInterviewStatus(rawInterviewStatus)
    ? rawInterviewStatus
    : wasFinishedInThisTab
    ? "scored"
    : null;

  const replaceToEvaluation = useCallback((interviewId: number) => {
    const path = `/evaluation?interview_id=${interviewId}`;
    if (typeof window === "undefined") {
      setLocation(path);
      return;
    }
    try {
      window.history.replaceState({ [INTERVIEW_FINISHED_STATE_KEY]: true }, "", path);
      window.dispatchEvent(new Event("popstate"));
    } catch {
      setLocation(path);
    }
  }, [setLocation]);

  useEffect(() => {
    if (!terminalInterviewStatus) {
      return;
    }

    finishInterviewLockedRef.current = true;
    setIsFinishInterviewLocked(true);
    setTerminalInterviewGuard({
      status: terminalInterviewStatus,
      interviewId: resolvedInterviewId,
    });

    if (typeof resolvedInterviewId === "number") {
      replaceToEvaluation(resolvedInterviewId);
    }
  }, [replaceToEvaluation, resolvedInterviewId, terminalInterviewStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const refreshInterviewStatus = () => {
      setIsInterviewStatusChecking(true);
      Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/current-interview/documents"] }),
      ]).finally(() => {
        setIsInterviewStatusChecking(false);
      });
    };

    const onPageShow = () => {
      refreshInterviewStatus();
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [queryClient]);

  const enterTerminalInterviewGuard = useCallback((status: TerminalInterviewStatus = "finishing") => {
    finishInterviewLockedRef.current = true;
    setIsFinishInterviewLocked(true);
    setTerminalInterviewGuard({
      status,
      interviewId: resolvedInterviewId,
    });
    if (typeof resolvedInterviewId === "number") {
      rememberFinishedInterviewId(resolvedInterviewId);
    }
  }, [resolvedInterviewId]);

  const isConversationActionBlockedByInterviewStatus =
    isInterviewStatusChecking ||
    terminalInterviewStatus !== null ||
    terminalInterviewGuard !== null ||
    isFinishInterviewLocked ||
    asyncScoringStatus !== null;

  const previousInterviewIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (previousInterviewIdRef.current === undefined) {
      previousInterviewIdRef.current = currentInterviewId;
      return;
    }
    if (previousInterviewIdRef.current === currentInterviewId) return;
    previousInterviewIdRef.current = currentInterviewId;
    finishInterviewLockedRef.current = false;
    setIsFinishInterviewLocked(false);
    setTerminalInterviewGuard(null);
    setConversations([]);
    setCurrentInterimText("");
    setSttCorrectionDraft(null);
    setCounterpartPresentation(null);
    setIsAwaitingResponse(false);
    setIsPlayingResponse(false);
  }, [currentInterviewId, setIsAwaitingResponse]);
  const interviewAvatarAssets = useMemo(
    () => resolveInterviewAvatarAssets(currentAvatar),
    [currentAvatar]
  );
  const [patientEyePosition, setPatientEyePosition] = useState<EyePosition | undefined>();
  const [dentistEyePosition, setDentistEyePosition] = useState<EyePosition | undefined>();
  const [canonicalPoseImageUrls, setCanonicalPoseImageUrls] = useState<CanonicalPoseImageUrls | undefined>();
  // viseme画像をプリロード（切り替え時ののっぺらぼう防止）
  useEffect(() => {
    void preloadImageUrls([
      interviewAvatarAssets.mouthImageUrl,
      ...Object.values(interviewAvatarAssets.visemeImageUrls),
      ...Object.values(canonicalPoseImageUrls ?? {}),
    ]);
  }, [canonicalPoseImageUrls, interviewAvatarAssets]);

  useEffect(() => {
    let cancelled = false;
    setCanonicalPoseImageUrls(undefined);
    if (!currentAvatar) return () => {
      cancelled = true;
    };

    fetchCanonicalPoseImageUrls(currentAvatar).then((urls) => {
      if (!cancelled) {
        setCanonicalPoseImageUrls(urls);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentAvatar]);
  useEffect(() => {
    if (currentAvatar) {
      fetchEyePosition(currentAvatar).then(setPatientEyePosition);
    }
  }, [currentAvatar]);
  useEffect(() => {
    if (currentAvatar?.type === 'dentist') {
      fetchEyePosition(currentAvatar).then(setDentistEyePosition);
    }
  }, [currentAvatar]);

  const jawMotionConfig = useMemo(
    () => currentAvatar?.type === 'dentist'
      ? {
          ...(isMaleDentistFrontbaseAvatar(currentAvatar, interviewAvatarAssets)
            ? MALE_DENTIST_FRONTBASE_JAW_MOTION
            : DENTIST_JAW_MOTION),
          maskImage: `url(${interviewAvatarAssets.jawMaskUrl})`,
        }
      : { ...PATIENT_JAW_MOTION, maskImage: `url(${interviewAvatarAssets.jawMaskUrl})` },
    [currentAvatar, interviewAvatarAssets]
  );

  // アバタータイプ切り替え
  const switchAvatarTypeMutation = useMutation({
    mutationFn: async (avatarType: 'patient' | 'dentist') => {
      const response = await apiRequest("POST", "/api/avatar/switch-type", { avatarType });
      return response.json();
    },
    onSuccess: async () => {
      setConversations([]);
      setCurrentInterimText("");
      setSttCorrectionDraft(null);
      await queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
      await refetchAvatar();
    },
    onError: (error: unknown) => {
      setErrorMessage(toUserFacingError(error, "conversation").description);
      setIsErrorOpen(true);
    },
  });

  const handleSwitchAvatarType = (avatarType: 'patient' | 'dentist') => {
    setMobilePanelView("main");
    setStageView("interview");
    switchAvatarTypeMutation.mutate(avatarType);
  };

  // 共有ref群
  const sharedRefs = {
    wsClientRef,
    sessionIdRef,
    recognitionClientIdRef,
    sttSessionMessageIdRef,
    sttPreviousInterimTextRef,
    sttConfirmedTextRef,
    sttReadyRef,
  };

  useEffect(() => {
    setAgentDebugWsSender((payload) => {
      wsClientRef.current?.send({ type: 'agent_debug_trace', payload });
    });
    // #region agent log
    agentDebugLog('H5', 'interview.tsx:mount', 'avatar_playback_mode', {
      avatarPlaybackMode,
      avatarType: currentAvatar?.type ?? null,
      hasDubbingVideo: !!(currentAvatar as any)?.videoFiles?.dubbingVideo,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
    // #endregion
    return () => setAgentDebugWsSender(null);
  }, [avatarPlaybackMode, currentAvatar?.type, currentAvatar]);

  // STT設定取得（無音閾値）
  const { data: sttSettingsData } = useQuery({
    queryKey: ['stt-settings'],
    queryFn: async () => {
      const res = await fetch('/api/stt-settings', { credentials: 'include' });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const audioOptions: AudioRecordingOptions = {};
  if (sttSettingsData?.settings) {
    const silenceSetting = sttSettingsData.settings.find(
      (s: any) => s.setting_key === 'silence_threshold_db'
    );
    if (silenceSetting) {
      audioOptions.silenceThresholdDb = parseFloat(silenceSetting.setting_value);
    }
  }

  // 録音フック
  const audioRecording = useAudioRecording(
    sharedRefs,
    { toast, setIsAwaitingResponse },
    audioOptions,
  );

  const handleStartRecordingWithMediaUnlock = useCallback(() => {
    if (isConversationActionBlockedByInterviewStatus) return;
    sttCorrectionRequestedRef.current = false;
    // iOS: ユーザー操作のうちに音声セッションを起動し、面接中ずっと維持する。
    // これがないと応答クリップ到着時に <video> の unmute が拒否され再生が止まる。
    startAudioSessionKeepAlive();
    requestMediaElementUnlock();
    void unlockMediaPlayback();
    audioRecording.handleStartRecording();
  }, [audioRecording, isConversationActionBlockedByInterviewStatus]);

  const handleSendAudioWithMediaUnlock = useCallback(() => {
    if (isConversationActionBlockedByInterviewStatus) return;
    sttCorrectionRequestedRef.current = false;
    startAudioSessionKeepAlive();
    requestMediaElementUnlock();
    void unlockMediaPlayback();
    audioRecording.handleSendAudio();
  }, [audioRecording, isConversationActionBlockedByInterviewStatus]);

  const handleCancelRecordingPreparation = useCallback(() => {
    audioRecording.handleStopRecording('user');
  }, [audioRecording]);

  const handleCancelAwaitingResponse = useCallback(() => {
    setIsAwaitingResponse(false);
    toast({
      title: awaitingResponseStage === "patient"
        ? `${currentAvatar?.type === 'dentist' ? '歯科医師' : patientCounterpartRole}の回答待ちを中止しました`
        : "聞き取りを中止しました",
      description: "マイクを押して、もう一度話せます。",
    });
  }, [awaitingResponseStage, currentAvatar?.type, patientCounterpartRole, setIsAwaitingResponse, toast]);

  const submitSttText = useCallback((draft: SttCorrectionDraft, text: string): boolean => {
    if (isConversationActionBlockedByInterviewStatus) return false;
    const submittedText = text.trim();
    if (!submittedText) {
      toast({
        title: "送信できません",
        description: "聞き取り結果が空です。文字を入力するか、録り直してください。",
        variant: "destructive",
      });
      return false;
    }

    const wsClient = wsClientRef.current;
    if (!wsClient || !wsClient.isConnected()) {
      toast({
        title: "送信できません",
        description: "通信が切れています。再接続後にもう一度お試しください。",
        variant: "destructive",
      });
      return false;
    }

    lastSubmittedSttRef.current = {
      rawText: draft.rawText.trim(),
      submittedAt: Date.now(),
    };
    sttCorrectionRequestedRef.current = false;
    setSttCorrectionDraft(null);
    setCurrentInterimText("");
    setAwaitingResponseStage("patient");
    setIsAwaitingResponse(true);
    setConversations(prev => [
      ...prev,
      {
        id: `msg-question-corrected-${Date.now()}`,
        type: 'question' as const,
        text: submittedText,
        timestamp: new Date(),
        isInterim: false,
      },
    ]);

    wsClient.send({
      type: 'user_message',
      payload: {
        text: submittedText,
        rawSttText: draft.rawText,
        sttSource: draft.source,
        correctionApplied: submittedText !== draft.rawText.trim(),
        sttLowConfidence: Boolean(draft.lowConfidence),
        sttConfidence: draft.confidence ?? null,
        sttThreshold: draft.threshold ?? null,
        recognitionClientId: draft.recognitionClientId ?? null,
      },
    });
    return true;
  }, [isConversationActionBlockedByInterviewStatus, setIsAwaitingResponse, toast, wsClientRef]);

  const handleSttCorrectionDraft = useCallback((draft: SttCorrectionDraft) => {
    if (
      draft.recognitionClientId
      && recognitionClientIdRef.current
      && draft.recognitionClientId !== recognitionClientIdRef.current
    ) {
      return "ignored";
    }

    if (Date.now() < suppressSttDraftUntilRef.current) {
      return "ignored";
    }

    const rawText = draft.rawText.trim();
    const lastSubmitted = lastSubmittedSttRef.current;
    if (lastSubmitted?.rawText === rawText && Date.now() - lastSubmitted.submittedAt < 5000) {
      return "ignored";
    }

    if (!sttCorrectionRequestedRef.current) {
      const mobileUiTestWindow = window as typeof window & {
        __mobileUiHoldSttFinal?: boolean;
        __mobileUiReleaseSttFinal?: () => boolean;
      };
      let shouldHoldSttFinal = false;
      try {
        shouldHoldSttFinal = window.localStorage.getItem("mobileUiStateTest") === "1"
          && mobileUiTestWindow.__mobileUiHoldSttFinal === true;
      } catch {
        // localStorageを利用できない環境では通常どおり確定結果を送信する。
      }
      if (
        shouldHoldSttFinal
      ) {
        mobileUiTestWindow.__mobileUiReleaseSttFinal = () => {
          mobileUiTestWindow.__mobileUiHoldSttFinal = false;
          delete mobileUiTestWindow.__mobileUiReleaseSttFinal;
          return submitSttText(draft, draft.text);
        };
        return "submitted";
      }

      if (submitSttText(draft, draft.text)) {
        return "submitted";
      }
      setSttCorrectionDraft(draft);
      setIsAwaitingResponse(false);
      return "editing";
    }

    sttCorrectionRequestedRef.current = false;
    setSttCorrectionDraft((prev) => {
      if (prev?.rawText.trim() === rawText) {
        return { ...prev, ...draft, text: prev.text };
      }
      return draft;
    });
    setIsAwaitingResponse(false);
    return "editing";
  }, [setIsAwaitingResponse, submitSttText]);

  const handleSubmitSttCorrection = useCallback(() => {
    const draft = sttCorrectionDraft;
    if (!draft) return;
    submitSttText(draft, draft.text);
  }, [sttCorrectionDraft, submitSttText]);

  const handleUpdateSttCorrectionText = useCallback((text: string) => {
    setSttCorrectionDraft(prev => prev ? { ...prev, text } : prev);
  }, []);

  const handleRerecordFromSttCorrection = useCallback(() => {
    suppressSttDraftUntilRef.current = Date.now() + 800;
    sttCorrectionRequestedRef.current = false;
    setSttCorrectionDraft(null);
    setCurrentInterimText("");
    setIsAwaitingResponse(false);
    handleStartRecordingWithMediaUnlock();
  }, [handleStartRecordingWithMediaUnlock, setIsAwaitingResponse]);

  // WebSocket接続フック
  useInterviewWebSocket(currentAvatar, sharedRefs, {
    setConversations,
    setCurrentInterimText,
    collectDebugState: collectWebSocketDebugState,
    setFsmState,
    setPatientState,
    setProgressPatientState,
    setTargetCategories,
    setCounterpartPresentation,
    setLastTiming,
    setIsAwaitingResponse,
    setIsPlayingResponse,
    setIsSessionReady: setIsInterviewSessionReady,
    setIsRecording: (val: any) => {
      // segment_text/errorハンドラからの直接リセットはuseAudioRecordingのisRecordingRefも更新
      if (val === false) {
        audioRecording.isRecordingRef.current = false;
      }
    },
    setCurrentVideoUrl,
    setCurrentVisemes,
    setRecordingTime: () => {},
    toast,
    handleStopRecording: audioRecording.handleStopRecording,
    resetRecordingAfterSessionMissing: audioRecording.resetRecordingAfterSessionMissing,
    restartRecordingAfterReconnect: handleStartRecordingWithMediaUnlock,
    stopSilenceDetection: audioRecording.stopSilenceDetection,
    audioStreamRef: audioRecording.audioStreamRef,
    audioContextRef: audioRecording.audioContextRef,
    isRecording: audioRecording.isRecording,
    isRecordingRef: audioRecording.isRecordingRef,
    isStoppingRef: audioRecording.isStoppingRef,
    enqueueResponseClip: enqueueResponseClipFromQueue,
    clearResponseClips: clearClipQueue,
    onInterviewClosedByScoring: () => enterTerminalInterviewGuard("finishing"),
    onSttCorrectionDraft: handleSttCorrectionDraft,
  });

  // respondMutation（HTTP APIフォールバック）
  const respondMutation = useMutation({
    mutationFn: async (audioData: FormData) => {
      const response = await fetch("/api/respond", {
        method: "POST",
        body: audioData,
        credentials: "include",
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json() as Promise<ResponseResult>;
    },
    onSuccess: (result) => {
      const questionText = result.questionText?.trim() ? result.questionText : (localUserQuestionRef.current || "（質問内容不明）");
      setConversations(prev => [
        ...prev,
        { type: 'question', text: questionText, timestamp: new Date() },
        { type: 'response', text: result.responseText, timestamp: new Date() },
      ]);
      localUserQuestionRef.current = "";
      setCurrentVideoUrl(result.videoUrl || null);
      setCurrentVisemes(null);
      setIsPlayingResponse(true);
    },
    onError: (error: unknown) => {
      setErrorMessage(toUserFacingError(error, "conversation").description);
      setIsErrorOpen(true);
    },
  });

  // マウント時キャッシュクリア
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
  }, [queryClient]);

  // Enterキー / Ctrl+Shift+A / V/C/D キーバインド
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
        event.preventDefault();
        if (isConversationActionBlockedByInterviewStatus) return;
        if (currentAvatar?.type === 'dentist' && currentUser?.participantCode) return;
        if (audioRecording.isRecording) {
          if (
            audioRecording.isRecorderReady &&
            !audioRecording.isStoppingRef.current &&
            !respondMutation.isPending
          ) {
            handleSendAudioWithMediaUnlock();
          }
        } else {
          if (!respondMutation.isPending && !isPlayingResponse && !isAwaitingResponse) {
            handleStartRecordingWithMediaUnlock();
          }
        }
      }
    };

    const handleDebugKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        if (!isStaff) return;
        if (currentAvatar?.type === 'dentist') {
          handleOpenDentistSystemPromptModal();
        } else {
          handleOpenDebugModal();
        }
      }
    };
    const handleAvatarPlaybackModeKeyPress = (event: KeyboardEvent) => {
      if (event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "v") {
        event.preventDefault();
        switchAvatarPlaybackMode("video");
      } else if (key === "c") {
        event.preventDefault();
        switchAvatarPlaybackMode("canvas");
      } else if (key === "d") {
        event.preventDefault();
        switchAvatarPlaybackMode("dom");
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('keydown', handleDebugKeyPress);
    window.addEventListener('keydown', handleAvatarPlaybackModeKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('keydown', handleDebugKeyPress);
      window.removeEventListener('keydown', handleAvatarPlaybackModeKeyPress);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audioRecording.isRecording,
    audioRecording.isRecorderReady,
    handleSendAudioWithMediaUnlock,
    handleStartRecordingWithMediaUnlock,
    respondMutation.isPending,
    isPlayingResponse,
    isAwaitingResponse,
    isConversationActionBlockedByInterviewStatus,
    currentAvatar,
    isStaff,
    currentUser?.participantCode,
    switchAvatarPlaybackMode,
  ]);

  const handleOpenDebugModal = async () => {
    try {
      const response = await fetch('/api/avatar/debug-info', { credentials: 'include' });
      if (!response.ok) throw new Error('デバッグ情報の取得に失敗しました');
      const data = await response.json();
      setDebugInfo(data);
      setIsDebugModalOpen(true);
    } catch (error) {
      toast({ title: "エラー", description: "デバッグ情報の取得に失敗しました", variant: "destructive" });
    }
  };

  const handleOpenDentistSystemPromptModal = async () => {
    try {
      const response = await fetch('/api/dentist/system-prompt', { credentials: 'include' });
      if (!response.ok) throw new Error('システムプロンプトの取得に失敗しました');
      const data = await response.json();
      setDentistSystemPrompt(data.systemPrompt || '');
      setDentistName(data.dentistName || '');
      setIsDentistSystemPromptModalOpen(true);
    } catch (error) {
      toast({ title: "エラー", description: "システムプロンプトの取得に失敗しました", variant: "destructive" });
    }
  };

  // 会話終了とスコアリング
  const finishInterviewMutation = useMutation({
    mutationFn: async () => {
      const interviewId = wsClientRef.current?.getCurrentInterviewId() ?? resolvedInterviewId;
      if (typeof interviewId !== 'number') {
        throw new Error('採点対象の面接を確認できません。画面を再読み込みしてください。');
      }
      const response = await apiRequest('POST', '/api/finish-interview', { interviewId });
      return await response.json() as FinishInterviewResponse;
    },
    onSuccess: async (data) => {
      queryClient.removeQueries({ queryKey: ["/api/score"] });

      if (data?.status === "accepted" && data.jobId) {
        setAsyncScoringStatus({
          jobId: data.jobId,
          status: "pending",
          interviewId: data.interviewId ?? null,
        });
        toast({
          title: "採点を開始しました",
          description: "混雑時は数分かかります。この画面のままお待ちください。",
        });

        try {
          const completed = await waitForScoringCompletion(data.jobId, setAsyncScoringStatus);
          queryClient.removeQueries({ queryKey: ["/api/score"] });
          setAsyncScoringStatus(null);
          toast({ title: "採点が完了しました", description: "評価ページに移動します。" });
          const nextInterviewId = completed.interviewId ?? data.interviewId;
          rememberFinishedInterviewId(nextInterviewId);
          setLocation(
            typeof nextInterviewId === "number"
              ? `/evaluation?interview_id=${nextInterviewId}`
              : "/evaluation",
            { replace: true, state: { [INTERVIEW_FINISHED_STATE_KEY]: true } },
          );
        } catch {
          setAsyncScoringStatus(null);
          // finish は server 側で受理済みなので、採点状況の polling だけ失敗しても
          // 同じ interviewId への追加発話は再開させず、評価ページへ移す。
          finishInterviewLockedRef.current = true;
          setIsFinishInterviewLocked(true);
          const nextInterviewId = data.interviewId ?? null;
          rememberFinishedInterviewId(nextInterviewId);
          toast({
            title: "採点状況の確認に失敗しました",
            description: "評価ページに移動します。結果がまだ生成中の場合は、少し待ってから再読み込みしてください。",
            variant: "destructive",
          });
          setLocation(
            typeof nextInterviewId === "number"
              ? `/evaluation?interview_id=${nextInterviewId}`
              : "/evaluation",
            { replace: true, state: { [INTERVIEW_FINISHED_STATE_KEY]: true } },
          );
        }
        return;
      }

      toast({ title: "会話を終了しました", description: "スコアリングが完了しました。評価ページに移動します。" });
      const nextLocation =
        typeof data?.interviewId === 'number'
          ? `/evaluation?interview_id=${data.interviewId}`
          : "/evaluation";
      rememberFinishedInterviewId(data?.interviewId);
      setLocation(nextLocation, { replace: true, state: { [INTERVIEW_FINISHED_STATE_KEY]: true } });
    },
    onError: (error: any) => {
      finishInterviewLockedRef.current = false;
      setIsFinishInterviewLocked(false);
      toast({ ...toUserFacingError(error, "scoring"), variant: "destructive" });
    },
  });

  const handleFinishInterview = () => {
    if (finishInterviewLockedRef.current || finishInterviewMutation.isPending || asyncScoringStatus) {
      return;
    }
    finishInterviewLockedRef.current = true;
    setIsFinishInterviewLocked(true);
    finishInterviewMutation.mutate();
  };

  const handleRestartInterview = () => {
    if (audioRecording.isRecording) audioRecording.handleStopRecording('user');
    finishInterviewLockedRef.current = false;
    setIsFinishInterviewLocked(false);
    setTerminalInterviewGuard(null);
    setConversations([]);
    setCurrentInterimText("");
    setSttCorrectionDraft(null);
    audioRecording.stopSilenceDetection();
    if (isExperimentDirectEntryUser(currentUser) && currentUser?.experimentEntryTarget) {
      startFreshExperimentDirectInterview(currentUser.experimentEntryTarget)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
          setLocation("/interview");
        })
        .catch((error) => {
          console.error("実証実験の直接面接再開に失敗しました:", error);
          setLocation("/interview");
        });
      return;
    }
    setLocation("/select-filter");
  };

  const handleVideoEnded = useCallback(() => {
    const sessionId = sessionIdRef.current;
    const wsConnected = !!wsClientRef.current?.isConnected();
    // #region agent log
    // H7: onResponseEnded が interview.tsx 側まで到達したことを記録。
    //     ここから audio_end 送信、isPlayingResponse=false 遷移、次ターン待ちまでが繋がる。
    agentDebugLog('H7', 'interview.tsx:handleVideoEnded', 'on_response_ended_received', {
      sessionId,
      wsConnected,
    });
    // #endregion
    if (sessionId) {
      wsClientRef.current?.send({
        type: 'audio_end',
        payload: {
          sessionId,
          timestamp: new Date().toISOString(),
        },
      });
      // #region agent log
      agentDebugLog('H7', 'interview.tsx:handleVideoEnded', 'audio_end_sent', {
        sessionId,
        wsConnected,
      });
      // #endregion
    } else {
      // #region agent log
      agentDebugLog('H7', 'interview.tsx:handleVideoEnded', 'audio_end_skipped_no_session', {
        wsConnected,
      });
      // #endregion
    }

    setIsPlayingResponse(false);
    setCurrentVideoUrl(null);
    setCurrentVisemes(null);
  }, []);

  const handleClipEnded = useCallback(() => {
    clipQueueOnEnded();
    handleVideoEnded();
  }, [clipQueueOnEnded, handleVideoEnded]);

  useEffect(() => {
    return () => {
      clearClipQueue();
    };
  }, [clearClipQueue]);

  // #region agent log
  // H7: マイクボタンが押せない状態（micDisabled=true）が解除されないまま固まったかを追跡する。
  //     micDisabled は早期 return 後で宣言されているため、ここでは依存 state から直接再計算する。
  useEffect(() => {
    const shouldDisableDentistReportInputLocal =
      currentAvatar?.type === 'dentist' && isExperimentParticipant;
    const computedMicDisabled =
      isInterviewStatusChecking ||
      !!terminalInterviewStatus ||
      !!terminalInterviewGuard ||
      isFinishInterviewLocked ||
      finishInterviewMutation.isPending ||
      asyncScoringStatus !== null ||
      shouldDisableDentistReportInputLocal ||
      respondMutation.isPending ||
      isPlayingResponse ||
      isAwaitingResponse;
    agentDebugLog('H7', 'interview.tsx:micDisabled', 'state_changed', {
      sessionId: sessionIdRef.current,
      micDisabled: computedMicDisabled,
      isPlayingResponse,
      isAwaitingResponse,
      respondPending: respondMutation.isPending,
      interviewStatusChecking: isInterviewStatusChecking,
      terminalInterviewStatus,
      terminalGuardStatus: terminalInterviewGuard?.status ?? null,
      isFinishInterviewLocked,
      finishInterviewPending: finishInterviewMutation.isPending,
      asyncScoringStatus: asyncScoringStatus?.status ?? null,
      shouldDisableDentistReportInput: shouldDisableDentistReportInputLocal,
      avatarType: currentAvatar?.type ?? null,
    });
  }, [
    currentAvatar?.type,
    isExperimentParticipant,
    respondMutation.isPending,
    isPlayingResponse,
    isAwaitingResponse,
    isInterviewStatusChecking,
    terminalInterviewStatus,
    terminalInterviewGuard,
    isFinishInterviewLocked,
    finishInterviewMutation.isPending,
    asyncScoringStatus,
  ]);

  // H7: Page Lifecycle / Visibility / Freeze 検知。
  //     Android Chrome がタブを frozen 化すると JS と setInterval が止まり、
  //     ユーザーから見るとフリーズに見える。再開時刻と凍結時間を記録する。
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleVisibility = () => {
      // タブ前面復帰時、suspended になった keep-alive を resume して音声セッションを保つ。
      if (document.visibilityState === 'visible') {
        resumeAudioSessionKeepAlive();
      }
      agentDebugLog('H7', 'interview.tsx:visibility', 'visibility_change', {
        sessionId: sessionIdRef.current,
        visibilityState: document.visibilityState,
        hidden: document.hidden,
      });
    };

    const handleLifecycle = (eventName: string) => (event: Event) => {
      agentDebugLog('H7', 'interview.tsx:lifecycle', eventName, {
        sessionId: sessionIdRef.current,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
        persisted: 'persisted' in event ? (event as PageTransitionEvent).persisted : undefined,
      });
    };

    const onPageHide = handleLifecycle('pagehide');
    const onPageShow = handleLifecycle('pageshow');
    const onFreeze = handleLifecycle('freeze');
    const onResume = handleLifecycle('resume');

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    // freeze/resume は Chromium 系のみサポートするが、addEventListener は安全に no-op となる。
    document.addEventListener('freeze', onFreeze as EventListener);
    document.addEventListener('resume', onResume as EventListener);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('freeze', onFreeze as EventListener);
      document.removeEventListener('resume', onResume as EventListener);
      stopAudioSessionKeepAlive();
    };
  }, []);

  // H7: UI スレッド停止検知（heartbeat）。
  //     1 秒ごとに wall-clock を比較し、想定外に大きく遅れていれば「JSが止まっていた」
  //     証拠として記録する（Android タブ frozen, OS スリープ等）。
  useEffect(() => {
    const INTERVAL_MS = 1000;
    const SLEEP_THRESHOLD_MS = 2000; // 1秒 interval が 2秒以上ジャンプしたら異常
    let lastTickAt = Date.now();
    const timer = window.setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickAt;
      lastTickAt = now;
      if (delta >= SLEEP_THRESHOLD_MS) {
        agentDebugLog('H7', 'interview.tsx:heartbeat', 'js_sleep_detected', {
          sessionId: sessionIdRef.current,
          intervalMs: INTERVAL_MS,
          actualDeltaMs: delta,
          visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
        });
      }
    }, INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);
  // #endregion

  const handlePlaybackIssue = useCallback((issue: PlaybackIssueDetails) => {
    const sessionId = sessionIdRef.current;
    const payload = {
      ...issue,
      sessionId,
      currentVideoUrl,
      avatarId: currentAvatar?.id,
      avatarType: currentAvatar?.type,
      timestamp: new Date().toISOString(),
    };

    console.warn("[Interview] avatar playback issue:", payload);
    wsClientRef.current?.send({
      type: "playback_issue",
      payload,
    });
  }, [currentAvatar?.id, currentAvatar?.type, currentVideoUrl]);

  if (reloadSessionState !== "ready" || isAvatarLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-medical-blue" />
        <span className="ml-3 text-medical-gray">
          {reloadSessionState !== "ready" ? "面接セッションを準備中..." : "アバター情報を読み込み中..."}
        </span>
      </div>
    );
  }

  if (avatarError || !currentAvatar) {
    const msg = avatarError instanceof Error ? avatarError.message : "アバター情報の取得に失敗しました";
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">
          {msg}
          {msg.includes("No avatar selected") || msg.includes("No dentist selected") ? " 面接を開始してください。" : ""}
        </p>
        <div className="flex gap-2 justify-center">
          <Button
            onClick={() => {
              if (isExperimentDirectEntryUser(currentUser) && currentUser?.experimentEntryTarget) {
                startExperimentDirectInterview(currentUser.experimentEntryTarget)
                  .then(() => {
                    queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
                    setLocation("/interview");
                  })
                  .catch(() => setLocation("/interview"));
                return;
              }
              setLocation("/select-filter");
            }}
          >
            {isExperimentDirectEntryUser(currentUser) ? "面接を開始する" : "条件選択に戻る"}
          </Button>
          <Button variant="outline" onClick={() => refetchAvatar()}>再読み込み</Button>
        </div>
      </div>
    );
  }

  if (terminalInterviewGuard) {
    const copy = getTerminalInterviewStatusCopy(terminalInterviewGuard.status);
    return (
      <div
        className="fixed inset-0 top-[68px] flex items-center justify-center px-6"
        style={{
          background: "#FAFAF7",
          color: "#18181B",
          fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        }}
      >
        <div
          style={{
            width: "min(100%, 520px)",
            background: "#FFFFFF",
            border: "1px solid #E4E4E7",
            borderRadius: 12,
            padding: 24,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", color: "#71717A" }}>
            STEP 06・評価確認
          </div>
          <h1 style={{ marginTop: 8, marginBottom: 8, fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>
            {copy.title}
          </h1>
          <p style={{ margin: 0, color: "#71717A", fontSize: 13.5, lineHeight: 1.75 }}>
            {copy.description}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
            {typeof terminalInterviewGuard.interviewId === "number" && (
              <Button
                onClick={() => replaceToEvaluation(terminalInterviewGuard.interviewId as number)}
              >
                評価ページへ戻る
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isDentist = currentAvatar?.type === 'dentist';
  const shouldBypassDentistReport = isExperimentParticipant;
  // 聞き取りゲージは患者面接の間だけ出す。全体量は採点と同じ計算の点数に合わせる
  const visibleProgress = !isDentist ? applyLivePatientScore(interviewProgress, liveScore?.patient) : null;
  // 報告面接の間は、患者面接の結果・報告・総合の3本を出す
  const reportLiveScore = isDentist && !shouldBypassDentistReport && liveScore ? liveScore : null;
  // 課題文の画面とドックのバッジ: 患者面接は聞き取り、報告面接は報告できた項目をタスクごとに出す
  const reportProgress = reportLiveScore
    ? computeReportProgress(
        reportLiveScore,
        documentsData?.instruction?.tasks,
        documentsData?.instruction?.target_categories,
      )
    : null;
  const documentProgress = visibleProgress ?? reportProgress;
  const documentProgressKind = isDentist ? "report" : "interview";
  // 目安（8割）に届いたら次へ進むボタンを光らせる（患者面接は聞き取り、報告は総合）
  const progressReady = isDentist ? isOverallReady(reportLiveScore) : !!visibleProgress?.reachedTarget;
  const shouldDisableDentistReportInput = isDentist && shouldBypassDentistReport;
  const phaseStepperPhase: "patient" | "dentist" | "score" =
    isDentist && shouldBypassDentistReport
      ? "score"
      : isDentist
        ? "dentist"
        : "patient";
  const isTerminalInterviewBlocked =
    isInterviewStatusChecking || terminalInterviewStatus !== null || terminalInterviewGuard !== null;
  const isFinishingInterview =
    isTerminalInterviewBlocked
    || isConversationActionBlockedByInterviewStatus
    || isFinishInterviewLocked
    || finishInterviewMutation.isPending
    || asyncScoringStatus !== null;
  const intB = {
    bg: "#F7F5EF",
    surface: "#FFFFFF",
    ink: "#18181B",
    muted: "#71717A",
    subtle: "#A8A29E",
    line: "#E4E4E7",
    softBg: "#FAFAF7",
    stage: "#FFFFFF",
    stageDentist: "#FFFFFF",
  };

  const hasSttCorrectionDraft = sttCorrectionDraft !== null;
  const micDisabled = !isInterviewSessionReady || isFinishingInterview || shouldDisableDentistReportInput || respondMutation.isPending || isPlayingResponse || isAwaitingResponse || hasSttCorrectionDraft;
  const displayIsRecording = (isFinishingInterview || shouldDisableDentistReportInput || hasSttCorrectionDraft) ? false : audioRecording.isRecording;
  const displayIsAwaitingResponse = (isFinishingInterview || shouldDisableDentistReportInput || hasSttCorrectionDraft) ? false : isAwaitingResponse;
  const displayIsRecorderReady = (isFinishingInterview || shouldDisableDentistReportInput || hasSttCorrectionDraft) ? false : audioRecording.isRecorderReady;
  const activityStatus: InterviewActivityStatus | null = !isInterviewSessionReady
    ? "preparing"
    : displayIsAwaitingResponse
    ? (awaitingResponseStage === "patient" ? "patient" : "stt")
    : displayIsRecording
      ? (displayIsRecorderReady ? "recording" : "preparing")
      : null;
  const referenceNavigationDisabled = displayIsRecording || respondMutation.isPending || hasSttCorrectionDraft;
  const switchDisabled =
    switchAvatarTypeMutation.isPending ||
    audioRecording.isRecording ||
    hasSttCorrectionDraft ||
    isPlayingResponse ||
    isAwaitingResponse ||
    respondMutation.isPending;
  const toastClearanceHeight = showStaffDebugPanel ? 0 : 112;
  const mobilePrimaryDisabled = (isDentist || shouldBypassDentistReport)
    ? isFinishingInterview || switchDisabled
    : switchDisabled;
  const patientPrimaryDisabled = shouldBypassDentistReport
    ? isFinishingInterview || switchDisabled
    : switchDisabled;
  const mobileOverlayModals = (
    <>
      <ErrorModal
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
        message={errorMessage}
      />

      {isStaff && (
        <DebugModals
          isDentistSystemPromptModalOpen={isDentistSystemPromptModalOpen}
          setIsDentistSystemPromptModalOpen={setIsDentistSystemPromptModalOpen}
          dentistSystemPrompt={dentistSystemPrompt}
          dentistName={dentistName}
          isDebugModalOpen={isDebugModalOpen}
          setIsDebugModalOpen={setIsDebugModalOpen}
          debugInfo={debugInfo}
        />
      )}
    </>
  );

  // 会話ログの発言者アイコン（面接画面を見ているのは学習者本人なのでプロフィール画像を出す）
  const counterpartLogIcon = (
    <AvatarFaceIcon
      src={getAvatarImage(currentAvatar)}
      label={isDentist ? "歯科医師" : patientCounterpartName || patientCounterpartRole}
    />
  );
  const learnerLogIcon = <LearnerIcon showProfileImage />;

  if (interviewLayoutMode === "mobile") {
    const mobileStepLabel = shouldDisableDentistReportInput
      ? "STEP 05・スコア表示"
      : isDentist
        ? "STEP 05・歯科衛生士への報告"
        : "STEP 04・患者面接";
    const mobileTitle = shouldDisableDentistReportInput
      ? "スコアを確認する"
      : isDentist
        ? "歯科衛生士へ要点を報告"
        : patientCounterpartName ? `${patientCounterpartName}と話す` : `${patientCounterpartRole}に質問する`;
    const mobileCounterpartLabel = isDentist ? "歯科医師" : patientCounterpartRole;
    const mobileAvatarCrop = getMobileAvatarCrop(currentAvatar);

    const mobileReferencePanel = (() => {
      if (mobilePanelView === "log") {
        return (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 30,
              background: intB.bg,
              color: intB.ink,
              fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: "0 0 auto",
                padding: "12px 14px 10px",
                background: intB.surface,
                borderBottom: `1px solid ${intB.line}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", color: intB.muted }}>
                  {mobileStepLabel}
                </div>
                <div style={{ marginTop: 4, fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>
                  会話ログ
                </div>
              </div>
              <button
                type="button"
                onClick={goToInterview}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  border: `1px solid ${intB.line}`,
                  background: intB.surface,
                  color: intB.ink,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                }}
                aria-label="面接に戻る"
              >
                <ArrowLeft size={19} />
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "hidden", background: intB.surface }}>
              <ConversationLog
                conversations={conversations}
                currentInterimText={currentInterimText}
                counterpartLabel={mobileCounterpartLabel}
                counterpartBubbleBg={isDentist ? "#F5F3FF" : "#F4F4F5"}
                counterpartIcon={counterpartLogIcon}
                studentIcon={learnerLogIcon}
                emptyPlaceholder="マイクをタップで開始"
                height="100%"
              />
            </div>

            <div
              style={{
                flex: "0 0 auto",
                padding: "10px 14px max(14px, env(safe-area-inset-bottom))",
                background: intB.surface,
                borderTop: `1px solid ${intB.line}`,
              }}
            >
              <MobileWideButton label="面接に戻る" onClick={goToInterview} />
            </div>
          </div>
        );
      }

      if (stageView === "interview") return null;

      const isQuestionnaireView = stageView === "questionnaire";
      return (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 30,
            background: intB.bg,
            color: intB.ink,
            fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              padding: "10px 14px 12px",
              background: intB.surface,
              borderBottom: `1px solid ${intB.line}`,
            }}
          >
            <div
              style={{
                width: 42,
                height: 4,
                borderRadius: 999,
                background: intB.line,
                margin: "0 auto 12px",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", color: intB.muted }}>
                  {mobileStepLabel}
                </div>
                <div style={{ marginTop: 4, fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>
                  {isQuestionnaireView ? "問診票" : "課題文"}
                </div>
                {themeLabels.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <InterviewThemeChip labels={themeLabels} />
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={goToInterview}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  border: `1px solid ${intB.line}`,
                  background: intB.surface,
                  color: intB.ink,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                }}
                aria-label="面接に戻る"
              >
                <ArrowLeft size={19} />
              </button>
            </div>

            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 6,
                padding: 4,
                border: `1px solid ${intB.line}`,
                borderRadius: 999,
                background: intB.softBg,
              }}
            >
              <MobileDocumentTab active={!isQuestionnaireView} label="課題文" onClick={goToInstruction} />
              <MobileDocumentTab active={isQuestionnaireView} label="問診票" onClick={goToQuestionnaire} />
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "14px 14px 18px",
              background: intB.bg,
            }}
          >
            {isQuestionnaireView ? (
              documentsData?.questionnaireJson ? (
                <QuestionnaireDocument
                  json={documentsData.questionnaireJson}
                  patientName={documentsData.patient?.name ?? undefined}
                  meta={{
                    age: documentsData.patient?.age ?? undefined,
                    gender: documentsData.patient?.gender ?? undefined,
                    birthDate: documentsData.patient?.birthDate ?? undefined,
                  }}
                  compact
                />
              ) : documentsData?.questionnaire ? (
                <pre
                  style={{
                    margin: 0,
                    background: "#FDFCF7",
                    border: "1px solid #ECE5D4",
                    borderRadius: 12,
                    padding: 20,
                    fontSize: 13,
                    lineHeight: 1.8,
                    whiteSpace: "pre-wrap",
                    fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
                    color: intB.ink,
                  }}
                >
                  {documentsData.questionnaire}
                </pre>
              ) : (
                <div style={{ textAlign: "center", padding: 40, color: intB.muted, fontSize: 13 }}>
                  {questionnaireEmptyMessage(documentsPending, documentsError)}
                </div>
              )
            ) : documentsData?.instruction ? (
              <InstructionPreviewBody
                maxWidth={720}
                progress={documentProgress}
                progressKind={documentProgressKind}
                summary={documentsData.instruction.summary}
                targetCategories={documentsData.instruction.target_categories ?? []}
                categoryLabels={documentsData.instruction.target_category_labels}
                tasks={
                  documentsData.instruction.tasks ??
                  documentsData.instruction.task_descriptions?.map((t, i) => ({
                    no: i + 1,
                    title: t.title,
                    details: t.description ? [t.description] : [],
                  })) ??
                  []
                }
              />
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: intB.muted, fontSize: 13 }}>
                課題文を読み込み中...
              </div>
            )}
          </div>

          <div
            style={{
              flex: "0 0 auto",
              padding: "10px 14px max(14px, env(safe-area-inset-bottom))",
              background: intB.surface,
              borderTop: `1px solid ${intB.line}`,
            }}
          >
            <MobileWideButton label="面接に戻る" onClick={goToInterview} />
          </div>
        </div>
      );
    })();

    return (
      <div
        className="fixed inset-0 top-[68px] overflow-hidden"
        data-interview-layout="mobile"
        style={{
          background: intB.bg,
          color: intB.ink,
          fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ flex: "0 0 auto", padding: "14px 14px 10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                minWidth: 0,
                padding: "7px 10px",
                borderRadius: 999,
                background: "rgba(20,20,22,0.6)",
                border: "1px solid rgba(255,255,255,0.08)",
                overflowX: "auto",
              }}
            >
              <PhaseStepper phase={phaseStepperPhase} skipDentistReport={shouldBypassDentistReport} compact />
            </div>
            {!isDentist && !shouldBypassDentistReport && (
              <button
                type="button"
                onClick={handleFinishInterview}
                disabled={isFinishingInterview || switchDisabled}
                style={{
                  flex: "0 0 auto",
                  minHeight: 38,
                  borderRadius: 999,
                  border: `1px solid ${intB.line}`,
                  padding: "8px 10px",
                  background: intB.surface,
                  color: intB.ink,
                  cursor: isFinishingInterview || switchDisabled ? "not-allowed" : "pointer",
                  opacity: isFinishingInterview || switchDisabled ? 0.55 : 1,
                  fontFamily: "inherit",
                  fontSize: 11,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                報告せずに採点
              </button>
            )}
            <button
              type="button"
              data-ui-state={(isDentist || shouldBypassDentistReport) && isFinishingInterview ? "scoring" : "score-ready"}
              className={progressReady && !mobilePrimaryDisabled ? "interview-progress-glow" : undefined}
              onClick={(isDentist || shouldBypassDentistReport) ? handleFinishInterview : () => handleSwitchAvatarType("dentist")}
              disabled={mobilePrimaryDisabled}
              style={{
                flex: "0 0 auto",
                minHeight: 38,
                borderRadius: 999,
                border: "none",
                padding: "8px 13px",
                background: mobilePrimaryDisabled
                  ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
                  : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
                boxShadow: mobilePrimaryDisabled
                  ? "0 2px 6px rgba(0,0,0,0.25)"
                  : "0 0 0 4px rgba(255,138,61,0.18), 0 8px 22px rgba(255,107,26,0.45)",
                color: "#FFFFFF",
                cursor: mobilePrimaryDisabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.2,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              {(isDentist || shouldBypassDentistReport) && isFinishingInterview ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  採点中
                </>
              ) : shouldBypassDentistReport ? (
                "採点開始"
              ) : isDentist ? (
                "採点開始"
              ) : (
                "報告面接へ"
              )}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", color: intB.muted }}>
            {mobileStepLabel}
          </div>
          <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.25 }}>
            {mobileTitle}
          </div>
          {themeLabels.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <InterviewThemeChip labels={themeLabels} />
            </div>
          )}
          {visibleProgress && (
            <div style={{ marginTop: 12 }}>
              <InterviewProgressGauge
                progress={visibleProgress}
                gain={progressGain}
              />
            </div>
          )}
          {reportLiveScore && (
            <div style={{ marginTop: 12 }}>
              <ReportScoreGauges liveScore={reportLiveScore} />
            </div>
          )}
        </div>

        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            margin: "0 14px",
            borderRadius: 12,
            border: `1px solid ${intB.line}`,
            background: avatarPlaybackMode === "video" ? "#000000" : intB.surface,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AvatarStage
            currentAvatar={currentAvatar}
            assets={interviewAvatarAssets}
            currentVideoUrl={currentVideoUrl}
            currentVisemes={currentVisemes}
            canonicalPoseImageUrls={canonicalPoseImageUrls}
            isSpeakingWithVisemes={isSpeakingWithVisemes}
            disableIdleBlink={disableIdleBlink}
            patientEyePosition={patientEyePosition}
            dentistEyePosition={dentistEyePosition}
            visemeDebugEnabled={visemeDebugEnabled}
            avatarPlaybackMode={avatarPlaybackMode}
            avatarRenderer={avatarRenderer}
            isPlayingResponse={isPlayingResponse}
            jawMotionConfig={jawMotionConfig}
            visemePlaybackTuning={visemePlaybackTuning}
            frameFit="cover"
            contentTransform={mobileAvatarCrop.transform}
            contentTransformOrigin={mobileAvatarCrop.transformOrigin}
            onVideoEnded={handleVideoEnded}
            onPlaybackIssue={handlePlaybackIssue}
            clipBlobSrc={clipBlobSrc}
            clipAudioBuffer={clipAudioBuffer}
            onClipEnded={handleClipEnded}
          />
          <SttFloatingBubble
            visible={activityStatus !== null && !sttCorrectionDraft}
            text={currentInterimText}
            status={activityStatus ?? "recording"}
            responseWaitingTitle={responseWaitingTitle}
            compact
          />
          {sttCorrectionDraft && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                bottom: 12,
                zIndex: 90,
                transform: "translateX(-50%)",
                width: "min(calc(100% - 24px), 520px)",
                maxWidth: "calc(100vw - 28px)",
                pointerEvents: "auto",
              }}
            >
              <SttCorrectionComposer
                draft={sttCorrectionDraft}
                compact
                floating
                disabled={respondMutation.isPending || isAwaitingResponse}
                onChangeText={handleUpdateSttCorrectionText}
                onSubmit={handleSubmitSttCorrection}
                onRerecord={handleRerecordFromSttCorrection}
              />
            </div>
          )}
        </div>

        <div
          style={{
            flex: "0 0 auto",
            padding: "10px 14px 12px",
            background: intB.surface,
            borderTop: `1px solid ${intB.line}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={goToConversationLog}
              disabled={referenceNavigationDisabled}
              style={{
                flexShrink: 0,
                minHeight: 44,
                borderRadius: 999,
                border: "1px solid #E4E4E7",
                background: referenceNavigationDisabled ? "#F4F4F5" : "#FFFFFF",
                color: referenceNavigationDisabled ? "#A1A1AA" : "#27272A",
                cursor: referenceNavigationDisabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: 0.3,
                padding: "10px 16px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              <MessageSquare size={16} />
              会話ログ
            </button>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            flex: "0 0 auto",
            padding: "10px 14px max(14px, env(safe-area-inset-bottom))",
            background: intB.surface,
            borderTop: `1px solid ${intB.line}`,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 12,
            }}
          >
            <MobileDockButton
              icon={<FileCheck size={22} />}
              label="課題文"
              badge={documentProgress ? <DockProgressBadge progress={documentProgress} /> : null}
              onClick={goToInstruction}
              disabled={shouldDisableDentistReportInput || referenceNavigationDisabled}
            />
            <MobileDockMic
              isRecording={displayIsRecording}
              isAwaitingResponse={displayIsAwaitingResponse}
              awaitingResponseStage={awaitingResponseStage}
              isPending={respondMutation.isPending}
              isRecorderReady={displayIsRecorderReady}
              isSessionReady={isInterviewSessionReady}
              micDisabled={micDisabled}
              counterpartWaitingLabel={counterpartWaitingLabel}
              onStart={handleStartRecordingWithMediaUnlock}
              onSend={displayIsRecorderReady ? handleSendAudioWithMediaUnlock : undefined}
              onCancelPreparing={handleCancelRecordingPreparation}
              onCancelAwaitingResponse={handleCancelAwaitingResponse}
            />
            <MobileDockButton
              icon={<FileText size={22} />}
              label="問診票"
              onClick={goToQuestionnaire}
              disabled={shouldDisableDentistReportInput || referenceNavigationDisabled}
            />
          </div>
        </div>
        {mobileReferencePanel}
        {mobileOverlayModals}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 top-[68px] overflow-hidden"
      data-interview-layout={interviewLayoutMode}
      style={{
        background: intB.bg,
        color: intB.ink,
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        display: "grid",
        gridTemplateColumns: "1fr 360px",
      }}
    >
      {/* Left: avatar stage */}
      <div
        style={{
          position: "relative",
          background: isDentist ? intB.stageDentist : intB.stage,
          overflow: "hidden",
          transition: "background 0.4s",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Phase stepper top-left */}
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 20,
            zIndex: 5,
            padding: "7px 14px",
            borderRadius: 999,
            background: "rgba(20,20,22,0.6)",
            border: "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(12px)",
          }}
        >
          <PhaseStepper phase={phaseStepperPhase} skipDentistReport={shouldBypassDentistReport} />
        </div>

        {/* テーマ（課題文・問診票を開いても見えるよう overlay より前面）と聞き取りゲージ */}
        {(themeLabels.length > 0 || ((visibleProgress || reportLiveScore) && stageView === "interview")) && (
          <div
            style={{
              position: "absolute",
              top: 64,
              left: 20,
              right: 20,
              zIndex: 5,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {themeLabels.length > 0 && <InterviewThemeChip labels={themeLabels} />}
            {visibleProgress && stageView === "interview" && (
              <div style={{ width: 420, maxWidth: "100%" }}>
                <InterviewProgressGauge
                  progress={visibleProgress}
                  variant="regular"
                  gain={progressGain}
                />
              </div>
            )}
            {reportLiveScore && stageView === "interview" && (
              <div style={{ width: 420, maxWidth: "100%" }}>
                <ReportScoreGauges liveScore={reportLiveScore} variant="regular" />
              </div>
            )}
          </div>
        )}

        {/* Top-right CTA */}
        <div
          style={{
            position: "absolute",
            top: 16,
            right: 20,
            zIndex: 5,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {!isDentist ? (
            <>
              {!shouldBypassDentistReport && (
                <button
                  type="button"
                  onClick={handleFinishInterview}
                  disabled={isFinishingInterview || switchDisabled}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 16px",
                    borderRadius: 999,
                    background: "#FFFFFF",
                    border: "1px solid #E4E4E7",
                    color: "#18181B",
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: isFinishingInterview || switchDisabled ? "not-allowed" : "pointer",
                    opacity: isFinishingInterview || switchDisabled ? 0.55 : 1,
                    fontFamily: "inherit",
                  }}
                >
                  医師報告をせずに採点
                </button>
              )}
              <button
                type="button"
                className={progressReady && !patientPrimaryDisabled ? "interview-progress-glow" : undefined}
                onClick={shouldBypassDentistReport ? handleFinishInterview : () => handleSwitchAvatarType('dentist')}
                disabled={patientPrimaryDisabled}
                style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 18px",
                borderRadius: 999,
                background: patientPrimaryDisabled
                  ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
                  : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
                border: "none",
                color: "#fff",
                fontSize: 13,
                fontWeight: 800,
                cursor: patientPrimaryDisabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                letterSpacing: 0.3,
                boxShadow: patientPrimaryDisabled
                  ? "0 2px 6px rgba(0,0,0,0.25)"
                  : "0 0 0 4px rgba(255,138,61,0.18), 0 8px 22px rgba(255,107,26,0.5), 0 2px 6px rgba(0,0,0,0.25)",
                transition: "transform 150ms ease-out",
              }}
              onMouseEnter={(e) => {
                if (patientPrimaryDisabled) return;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {isFinishingInterview && shouldBypassDentistReport ? (
                <><Loader2 className="w-4 h-4 animate-spin" />採点中...</>
              ) : shouldBypassDentistReport ? (
                <>採点開始</>
              ) : (
                <>歯科医師への報告面接 →</>
              )}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={progressReady && !isFinishingInterview ? "interview-progress-glow" : undefined}
              onClick={handleFinishInterview}
              disabled={isFinishingInterview}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 18px",
                borderRadius: 999,
                background:
                  isFinishingInterview
                    ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
                    : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
                border: "none",
                color: "#fff",
                fontSize: 13,
                fontWeight: 800,
                cursor:
                  isFinishingInterview
                    ? "not-allowed"
                    : "pointer",
                fontFamily: "inherit",
                letterSpacing: 0.3,
                boxShadow:
                  isFinishingInterview
                    ? "0 2px 6px rgba(0,0,0,0.25)"
                    : "0 0 0 4px rgba(255,138,61,0.18), 0 8px 22px rgba(255,107,26,0.5), 0 2px 6px rgba(0,0,0,0.25)",
                transition: "transform 150ms ease-out",
              }}
              onMouseEnter={(e) => {
                if (isFinishingInterview) return;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {isFinishingInterview ? (
                <><Loader2 className="w-4 h-4 animate-spin" />採点中...</>
              ) : shouldDisableDentistReportInput ? (
                <>採点開始</>
              ) : (
                <>採点開始</>
              )}
            </button>
          )}
        </div>

        {/* Avatar stage（参照パネル操作で動画/音声を止めないため常時 mounted） */}
        <AvatarStage
          currentAvatar={currentAvatar}
          assets={interviewAvatarAssets}
          currentVideoUrl={currentVideoUrl}
          currentVisemes={currentVisemes}
          canonicalPoseImageUrls={canonicalPoseImageUrls}
          isSpeakingWithVisemes={isSpeakingWithVisemes}
          disableIdleBlink={disableIdleBlink}
          patientEyePosition={patientEyePosition}
          dentistEyePosition={dentistEyePosition}
          visemeDebugEnabled={visemeDebugEnabled}
          avatarPlaybackMode={avatarPlaybackMode}
          avatarRenderer={avatarRenderer}
          isPlayingResponse={isPlayingResponse}
          jawMotionConfig={jawMotionConfig}
          visemePlaybackTuning={visemePlaybackTuning}
          onVideoEnded={handleVideoEnded}
          onPlaybackIssue={handlePlaybackIssue}
          clipBlobSrc={clipBlobSrc}
          clipAudioBuffer={clipAudioBuffer}
          onClipEnded={handleClipEnded}
        />
        <SttFloatingBubble
          visible={stageView === "interview" && activityStatus !== null && !sttCorrectionDraft}
          text={currentInterimText}
          status={activityStatus ?? "recording"}
          responseWaitingTitle={responseWaitingTitle}
        />

        {/* Document overlay（問診票/課題文ビュー） */}
        {stageView !== "interview" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              paddingTop: themeLabels.length > 0 ? 108 : 72,
              paddingBottom: 220,
              paddingLeft: 24,
              paddingRight: 24,
              overflowY: "auto",
              background: "#FAFAF7",
              color: "#18181B",
              zIndex: 2,
            }}
          >
            {stageView === "questionnaire" ? (
              documentsData?.questionnaireJson ? (
                <QuestionnaireDocument
                  json={documentsData.questionnaireJson}
                  patientName={documentsData.patient?.name ?? undefined}
                  meta={{
                    age: documentsData.patient?.age ?? undefined,
                    gender: documentsData.patient?.gender ?? undefined,
                    birthDate: documentsData.patient?.birthDate ?? undefined,
                  }}
                />
              ) : documentsData?.questionnaire ? (
                <pre
                  style={{
                    maxWidth: 960,
                    margin: "0 auto",
                    background: "#FDFCF7",
                    border: "1px solid #ECE5D4",
                    borderRadius: 12,
                    padding: "28px 36px",
                    fontSize: 14,
                    lineHeight: 1.9,
                    whiteSpace: "pre-wrap",
                    fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
                  }}
                >
                  {documentsData.questionnaire}
                </pre>
              ) : (
                <div style={{ textAlign: "center", padding: 60, color: "#71717A" }}>{questionnaireEmptyMessage(documentsPending, documentsError)}</div>
              )
            ) : documentsData?.instruction ? (
              <InstructionPreviewBody
                progress={documentProgress}
                progressKind={documentProgressKind}
                summary={documentsData.instruction.summary}
                targetCategories={documentsData.instruction.target_categories ?? []}
                categoryLabels={documentsData.instruction.target_category_labels}
                tasks={
                  documentsData.instruction.tasks ??
                  documentsData.instruction.task_descriptions?.map((t, i) => ({
                    no: i + 1,
                    title: t.title,
                    details: t.description ? [t.description] : [],
                  })) ??
                  []
                }
              />
            ) : (
              <div style={{ textAlign: "center", padding: 60, color: "#71717A" }}>課題文を読み込み中...</div>
            )}
          </div>
        )}

        {/* Bottom controls: 課題文 ← Mic → 問診票 */}
        <div
          style={{
            position: "absolute",
            bottom: 80,
            left: 0,
            right: 0,
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          {/* 案3ツールバー: 課題文 / 中央(Mic or 面接に戻る) / 問診票 */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              background: "#FFFFFF",
              border: "1px solid #E4E4E7",
              borderRadius: 20,
              padding: "4px 20px",
              gap: 52,
              boxShadow: "0 6px 20px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            {/* Slot 1 (left): 課題文 */}
            <ToolbarSideButton
              icon={<FileCheck size={20} />}
              label="課題文"
              badge={documentProgress ? <DockProgressBadge progress={documentProgress} /> : null}
              state={
                referenceNavigationDisabled
                  ? "disabled"
                  : stageView === "instruction"
                  ? "active"
                  : "neutral"
              }
              onClick={goToInstruction}
            />

            {/* divider */}
            <div style={{ width: 1, height: 34, background: "#E4E4E7", marginBottom: 4 }} />

            {/* Slot 2 (center): 面接ビュー時=Mic（中に「話す」）、他ビュー時=面接に戻る */}
            {stageView === "interview" ? (
              displayIsAwaitingResponse ? (
                <button
                  type="button"
                  onClick={handleCancelAwaitingResponse}
                  disabled={respondMutation.isPending}
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: 44,
                    background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                    border: "3px solid #FFFFFF",
                    boxShadow: "0 0 0 8px rgba(245,158,11,0.18), 0 14px 36px rgba(217,119,6,0.45)",
                    color: "#fff",
                    cursor: respondMutation.isPending ? "wait" : "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    fontFamily: "inherit",
                    marginTop: -40,
                  }}
                >
                  <Loader2 size={26} className="animate-spin" />
                  <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.2 }}>
                    {awaitingResponseStage === "patient" ? counterpartWaitingLabel : "聞き取り中"}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.2 }}>中止</span>
                </button>
              ) : (
              !displayIsRecording ? (
                <button
                  type="button"
                  onClick={handleStartRecordingWithMediaUnlock}
                  disabled={micDisabled}
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: 44,
                    background: micDisabled
                      ? "linear-gradient(135deg, #A3A3A3 0%, #737373 100%)"
                      : "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
                    border: "3px solid #FFFFFF",
                    boxShadow: micDisabled
                      ? "0 4px 12px rgba(0,0,0,0.3)"
                      : "0 0 0 6px rgba(34,197,94,0.2), 0 12px 30px rgba(34,197,94,0.45)",
                    color: "#fff",
                    cursor: micDisabled ? "not-allowed" : "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    fontFamily: "inherit",
                    marginTop: -40,
                  }}
                >
                  {isInterviewSessionReady ? <Mic size={24} /> : <Loader2 size={24} className="animate-spin" />}
                  <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.2 }}>
                    {isInterviewSessionReady ? "タップして話す" : "準備中"}
                  </span>
                </button>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    marginTop: -40,
                  }}
                >
                  <button
                    type="button"
                    onClick={displayIsRecorderReady ? handleSendAudioWithMediaUnlock : handleCancelRecordingPreparation}
                    disabled={respondMutation.isPending}
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: 44,
                      background: displayIsRecorderReady
                        ? "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)"
                        : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                      border: "3px solid #FFFFFF",
                      boxShadow: "0 0 0 8px rgba(255,107,26,0.2), 0 14px 36px rgba(255,107,26,0.5)",
                      color: "#fff",
                      cursor: !respondMutation.isPending ? "pointer" : "wait",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      fontFamily: "inherit",
                    }}
                  >
                    {respondMutation.isPending ? (
                      <Loader2 size={26} className="animate-spin" />
                    ) : displayIsRecorderReady ? (
                      <>
                        <Send size={28} />
                        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>送信</span>
                      </>
                    ) : (
                      <>
                        <Loader2 size={26} className="animate-spin" />
                        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>準備中</span>
                        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.2 }}>タップで中止</span>
                      </>
                    )}
                  </button>
                </div>
              )
              )
            ) : (
              <button
                type="button"
                onClick={goToInterview}
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 44,
                  background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                  border: "3px solid #FFFFFF",
                  boxShadow: "0 0 0 6px rgba(59,130,246,0.18), 0 10px 24px rgba(37,99,235,0.45)",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  fontFamily: "inherit",
                  marginTop: -40,
                }}
              >
                <ArrowLeft size={26} />
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.2 }}>面接に戻る</span>
              </button>
            )}

            {/* divider */}
            <div style={{ width: 1, height: 34, background: "#E4E4E7", marginBottom: 4 }} />

            {/* Slot 3 (right): 問診票 */}
            <ToolbarSideButton
              icon={<FileText size={20} />}
              label="問診票"
              state={
                referenceNavigationDisabled
                  ? "disabled"
                  : stageView === "questionnaire"
                  ? "active"
                  : "neutral"
              }
              onClick={goToQuestionnaire}
            />
          </div>
        </div>
      </div>

      {/* Right: chat panel + (staff) debug */}
      <div
        style={{
          background: intB.surface,
          borderLeft: `1px solid ${intB.line}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Panel header */}
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${intB.line}` }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: -0.2 }}>
            会話の記録
          </div>
        </div>

        {/* Conversation log */}
        <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          <ConversationLog
            conversations={conversations}
            currentInterimText={currentInterimText}
            counterpartLabel={isDentist ? "歯科医師" : patientCounterpartRole}
            counterpartBubbleBg={isDentist ? "#F5F3FF" : "#F3F4F6"}
            counterpartIcon={counterpartLogIcon}
            studentIcon={learnerLogIcon}
          />
        </div>

        {sttCorrectionDraft && (
          <div style={{ padding: "10px 16px 0", background: intB.softBg }}>
            <SttCorrectionComposer
              draft={sttCorrectionDraft}
              disabled={respondMutation.isPending || isAwaitingResponse}
              onChangeText={handleUpdateSttCorrectionText}
              onSubmit={handleSubmitSttCorrection}
              onRerecord={handleRerecordFromSttCorrection}
            />
          </div>
        )}

        {/* Staff only: Debug panels */}
        {showStaffDebugPanel && (
          <div
            style={{
              borderTop: `1px solid ${intB.line}`,
              background: intB.softBg,
              maxHeight: "40vh",
              overflowY: "auto",
              padding: 8,
            }}
          >
            {isDentist
              ? <DentistFSMPanel fsmState={fsmState} timing={lastTiming} targetCategories={targetCategories} />
              : <PatientInterviewPanel patientState={patientState} targetCategories={targetCategories} />
            }
          </div>
        )}

        {/* Footer actions */}
        <div
          style={{
            padding: "10px 16px 14px",
            borderTop: `1px solid ${intB.line}`,
            display: "flex",
            gap: 8,
            background: intB.softBg,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={handleRestartInterview}
            variant="outline"
            className="h-9 text-sm"
            disabled={isFinishingInterview}
            style={{ marginLeft: "auto" }}
          >
            中止
          </Button>
        </div>
        {toastClearanceHeight > 0 && (
          <div
            aria-hidden
            style={{
              flex: "0 0 auto",
              height: toastClearanceHeight,
              background: intB.softBg,
            }}
          />
        )}
      </div>

      <ErrorModal
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
        message={errorMessage}
      />

      {isStaff && (
        <DebugModals
          isDentistSystemPromptModalOpen={isDentistSystemPromptModalOpen}
          setIsDentistSystemPromptModalOpen={setIsDentistSystemPromptModalOpen}
          dentistSystemPrompt={dentistSystemPrompt}
          dentistName={dentistName}
          isDebugModalOpen={isDebugModalOpen}
          setIsDebugModalOpen={setIsDebugModalOpen}
          debugInfo={debugInfo}
        />
      )}

    </div>
  );
}

function getMobileAvatarCrop(_avatar: Avatar): {
  transform: string;
  transformOrigin: string;
} {
  // 全アバターで共通: 顔中心が枠中央に来るように同じスケール・オリジンで中央クロップ
  return {
    transform: "scale(1.12)",
    transformOrigin: "50% 46%",
  };
}

function SttFloatingBubble({
  visible,
  text,
  status,
  responseWaitingTitle,
  compact = false,
}: {
  visible: boolean;
  text: string;
  status: InterviewActivityStatus;
  responseWaitingTitle: string;
  compact?: boolean;
}) {
  if (!visible) return null;

  const displayText = text.trim();
  const hasInterimText = status === "recording" && displayText.length > 0;
  const statusCopy: Record<InterviewActivityStatus, { title: string; description: string }> = {
    preparing: {
      title: "準備中",
      description: "マイクと音声認識を準備しています。",
    },
    recording: {
      title: "聞き取り中",
      description: "音声を受け取っています。話し終わったら送信してください。",
    },
    stt: {
      title: "聞き取り中",
      description: "話した内容を確認しています。そのままお待ちください。",
    },
    patient: {
      title: responseWaitingTitle,
      description: "回答が届くまで、そのままお待ちください。",
    },
  };
  const copy = statusCopy[status];
  const isRecordingStatus = status === "recording";

  return (
    <div
      aria-live="polite"
      style={{
        position: "absolute",
        left: "50%",
        top: undefined,
        bottom: compact ? 12 : 160,
        zIndex: compact ? 80 : 30,
        transform: "translateX(-50%)",
        width: compact ? "min(calc(100% - 24px), 520px)" : "min(calc(100% - 48px), 620px)",
        maxWidth: "calc(100vw - 28px)",
        margin: 0,
        padding: compact ? "11px 14px" : "14px 18px",
        borderRadius: 14,
        border: hasInterimText ? "1px solid rgba(228,228,231,0.96)" : "1px solid rgba(255,255,255,0.16)",
        background: hasInterimText ? "rgba(255,255,255,0.96)" : "rgba(24,24,27,0.88)",
        boxShadow: compact
          ? "0 10px 24px rgba(24,24,27,0.16), 0 2px 6px rgba(24,24,27,0.08)"
          : "0 12px 30px rgba(24,24,27,0.18), 0 2px 8px rgba(24,24,27,0.1)",
        color: hasInterimText ? "#18181B" : "#FFFFFF",
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        boxSizing: "border-box",
        pointerEvents: "none",
      }}
    >
      {hasInterimText ? (
        <>
          <div
            style={{
              fontSize: compact ? 11 : 12,
              fontWeight: 800,
              color: "#71717A",
              lineHeight: 1.25,
              marginBottom: 5,
            }}
          >
            あなたの声（認識中）
          </div>
          <div
            style={{
              display: "-webkit-box",
              WebkitLineClamp: compact ? 2 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontSize: compact ? 14 : 17,
              fontWeight: 800,
              lineHeight: 1.45,
              letterSpacing: compact ? 0 : -0.2,
              wordBreak: "break-word",
            }}
          >
            {displayText}
          </div>
        </>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: compact ? 10 : 14,
          }}
        >
          <div
            aria-hidden
            style={{
              width: compact ? 30 : 38,
              height: compact ? 30 : 38,
              borderRadius: 999,
              background: isRecordingStatus ? "rgba(21,128,61,0.18)" : "rgba(180,83,9,0.2)",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            <div
              style={{
                width: compact ? 13 : 16,
                height: compact ? 13 : 16,
                borderRadius: 999,
                background: isRecordingStatus ? "#15803D" : "#B45309",
                boxShadow: isRecordingStatus
                  ? "0 0 0 6px rgba(21,128,61,0.18)"
                  : "0 0 0 6px rgba(180,83,9,0.2)",
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: compact ? 14 : 17,
                fontWeight: 800,
                lineHeight: 1.35,
                letterSpacing: compact ? 0 : -0.2,
              }}
            >
              {copy.title}
            </div>
            <div
              style={{
                marginTop: 2,
                fontSize: compact ? 11.5 : 13,
                fontWeight: 700,
                lineHeight: 1.45,
                color: "rgba(255,255,255,0.76)",
                whiteSpace: compact ? "normal" : "nowrap",
              }}
            >
              {copy.description}
            </div>
          </div>
          <div
            aria-hidden
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: compact ? 3 : 4,
              flex: "0 0 auto",
            }}
          >
            {[16, 26, 20, 32].map((height, index) => (
              <span
                key={index}
                style={{
                  display: "block",
                  width: compact ? 4 : 5,
                  height: compact ? Math.max(12, height - 6) : height,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.78)",
                }}
              />
            ))}
          </div>
        </div>
      )}
      {!compact && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: "50%",
            bottom: -10,
            width: 18,
            height: 18,
            transform: "translateX(-50%) rotate(45deg)",
            background: hasInterimText ? "rgba(255,255,255,0.96)" : "rgba(24,24,27,0.88)",
            borderRight: hasInterimText ? "1px solid rgba(228,228,231,0.96)" : "1px solid rgba(255,255,255,0.16)",
            borderBottom: hasInterimText ? "1px solid rgba(228,228,231,0.96)" : "1px solid rgba(255,255,255,0.16)",
            borderBottomRightRadius: 3,
          }}
        />
      )}
    </div>
  );
}

function SttCorrectionComposer({
  draft,
  compact = false,
  floating = false,
  disabled = false,
  onChangeText,
  onSubmit,
  onRerecord,
}: {
  draft: SttCorrectionDraft | null;
  compact?: boolean;
  floating?: boolean;
  disabled?: boolean;
  onChangeText: (text: string) => void;
  onSubmit: () => void;
  onRerecord: () => void;
}) {
  if (!draft) return null;

  const changed = draft.text.trim() !== draft.rawText.trim();

  return (
    <div
      style={{
        border: "1px solid #E4E4E7",
        borderRadius: 8,
        background: "#FFFFFF",
        boxShadow: floating
          ? "0 18px 42px rgba(24,24,27,0.24), 0 2px 8px rgba(24,24,27,0.1)"
          : compact ? "0 10px 24px rgba(24,24,27,0.12)" : "0 14px 32px rgba(24,24,27,0.14)",
        padding: compact ? 12 : 16,
        marginBottom: floating ? 0 : compact ? 10 : 0,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#71717A", letterSpacing: 0.2 }}>
          聞き取り結果
        </div>
        {changed && (
          <div
            style={{
              borderRadius: 999,
              background: "#ECFEFF",
              color: "#0E7490",
              fontSize: 11,
              fontWeight: 800,
              padding: "3px 8px",
              whiteSpace: "nowrap",
            }}
          >
            修正あり
          </div>
        )}
      </div>
      <textarea
        value={draft.text}
        onChange={(event) => onChangeText(event.target.value)}
        rows={compact ? 3 : 4}
        autoFocus
        disabled={disabled}
        style={{
          width: "100%",
          resize: "vertical",
          border: "1px solid #E4E4E7",
          borderRadius: 8,
          background: "#FFFFFF",
          color: "#18181B",
          padding: "12px 13px",
          fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
          fontSize: compact ? 14 : 15,
          fontWeight: 700,
          lineHeight: 1.6,
          boxSizing: "border-box",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onRerecord}
          disabled={disabled}
          style={{
            minHeight: 38,
            borderRadius: 8,
            border: "1px solid #E4E4E7",
            background: "#FFFFFF",
            color: "#18181B",
            padding: "8px 13px",
            fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
            fontSize: 13,
            fontWeight: 800,
            cursor: disabled ? "wait" : "pointer",
          }}
        >
          録り直す
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled || !draft.text.trim()}
          style={{
            minHeight: 38,
            borderRadius: 8,
            border: "none",
            background: disabled || !draft.text.trim() ? "#A1A1AA" : "#18181B",
            color: "#FFFFFF",
            padding: "8px 16px",
            fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
            fontSize: 13,
            fontWeight: 800,
            cursor: disabled || !draft.text.trim() ? "not-allowed" : "pointer",
          }}
        >
          送信
        </button>
      </div>
    </div>
  );
}

function MobileWideButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        minHeight: 48,
        borderRadius: 8,
        border: "none",
        background: "#18181B",
        color: "#FFFFFF",
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function MobileDocumentTab({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: 34,
        borderRadius: 999,
        border: "none",
        background: active ? "#FFFFFF" : "transparent",
        color: active ? "#18181B" : "#71717A",
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        fontSize: 12,
        fontWeight: 800,
        cursor: "pointer",
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
      }}
    >
      {label}
    </button>
  );
}

function MobileDockButton({
  icon,
  label,
  onClick,
  disabled = false,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  badge?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        position: "relative",
        height: 78,
        width: "100%",
        borderRadius: 14,
        border: "1px solid #E4E4E7",
        background: disabled ? "#F4F4F5" : "#FFFFFF",
        color: disabled ? "#A1A1AA" : "#27272A",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        fontSize: 12,
        fontWeight: 800,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {icon}
      <span>{label}</span>
      {badge}
    </button>
  );
}

function MobileDockMic({
  isRecording,
  isAwaitingResponse,
  awaitingResponseStage,
  isPending,
  isRecorderReady,
  isSessionReady,
  micDisabled,
  counterpartWaitingLabel,
  recordingActionLabel = "送信",
  onStart,
  onSend,
  onCancelPreparing,
  onCancelAwaitingResponse,
}: {
  isRecording: boolean;
  isAwaitingResponse: boolean;
  awaitingResponseStage: "stt" | "patient" | null;
  isPending: boolean;
  isRecorderReady: boolean;
  isSessionReady: boolean;
  micDisabled: boolean;
  counterpartWaitingLabel: string;
  recordingActionLabel?: string;
  onStart: () => void;
  onSend?: () => void;
  onCancelPreparing: () => void;
  onCancelAwaitingResponse: () => void;
}) {
  if (isAwaitingResponse) {
    return (
      <button
        type="button"
        data-ui-state={awaitingResponseStage === "patient" ? "patient-response-wait" : "stt-final-wait"}
        onClick={onCancelAwaitingResponse}
        disabled={isPending}
        style={{
          width: 88,
          height: 88,
          borderRadius: 44,
          background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
          border: "3px solid #FFFFFF",
          boxShadow: "0 0 0 8px rgba(245,158,11,0.18), 0 14px 36px rgba(217,119,6,0.45)",
          color: "#fff",
          cursor: isPending ? "wait" : "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          fontFamily: "inherit",
        }}
      >
        <Loader2 size={26} className="animate-spin" />
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>
          {awaitingResponseStage === "patient" ? counterpartWaitingLabel : "聞き取り中"}
        </span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.2 }}>中止</span>
      </button>
    );
  }

  if (!isRecording) {
    return (
      <button
        type="button"
        data-ui-state="mic-idle"
        onClick={onStart}
        disabled={micDisabled}
        style={{
          width: 88,
          height: 88,
          borderRadius: 44,
          background: micDisabled
            ? "linear-gradient(135deg, #A3A3A3 0%, #737373 100%)"
            : "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
          border: "3px solid #FFFFFF",
          boxShadow: micDisabled
            ? "0 4px 12px rgba(0,0,0,0.3)"
            : "0 0 0 6px rgba(34,197,94,0.2), 0 12px 30px rgba(34,197,94,0.45)",
          color: "#fff",
          cursor: micDisabled ? "not-allowed" : "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          fontFamily: "inherit",
        }}
      >
        {isSessionReady ? <Mic size={24} /> : <Loader2 size={24} className="animate-spin" />}
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.2 }}>
          {isSessionReady ? "タップして話す" : "準備中"}
        </span>
      </button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <button
        type="button"
        data-ui-state={isRecorderReady ? "recording" : "mic-preparing"}
        onClick={isRecorderReady ? onSend : onCancelPreparing}
        disabled={isPending}
        style={{
          width: 88,
          height: 88,
          borderRadius: 44,
          background: isRecorderReady
            ? "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)"
            : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
          border: "3px solid #FFFFFF",
          boxShadow: "0 0 0 8px rgba(255,107,26,0.2), 0 14px 36px rgba(255,107,26,0.5)",
          color: "#fff",
          cursor: !isPending ? "pointer" : "wait",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          fontFamily: "inherit",
        }}
      >
        {isPending ? (
          <Loader2 size={26} className="animate-spin" />
        ) : isRecorderReady ? (
          <>
            <Send size={28} />
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>{recordingActionLabel}</span>
          </>
        ) : (
          <>
            <Loader2 size={26} className="animate-spin" />
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>準備中</span>
            <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.2 }}>タップで中止</span>
          </>
        )}
      </button>
    </div>
  );
}

function ToolbarSideButton({
  icon,
  label,
  state,
  onClick,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  state: "active" | "neutral" | "disabled";
  onClick: () => void;
  badge?: React.ReactNode;
}) {
  const styleByState: Record<typeof state, React.CSSProperties> = {
    active: {
      background: "#EFF6FF",
      color: "#2563EB",
    },
    neutral: {
      background: "transparent",
      color: "#52525B",
    },
    disabled: {
      background: "transparent",
      color: "#D4D4D8",
    },
  };
  const s = styleByState[state];
  return (
    <button
      type="button"
      disabled={state === "disabled"}
      onClick={onClick}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        padding: "4px 14px",
        borderRadius: 12,
        border: "none",
        cursor: state === "disabled" ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        fontSize: 10.5,
        fontWeight: state === "active" ? 800 : 700,
        letterSpacing: 0.3,
        transition: "background 120ms ease, color 120ms ease",
        minWidth: 64,
        ...s,
      }}
      onMouseEnter={(e) => {
        if (state === "active" || state === "disabled") return;
        e.currentTarget.style.background = "#F4F4F5";
      }}
      onMouseLeave={(e) => {
        if (state === "active" || state === "disabled") return;
        e.currentTarget.style.background = "transparent";
      }}
    >
      {icon}
      <span>{label}</span>
      {badge}
    </button>
  );
}

function PhaseStepper({
  phase,
  compact = false,
  skipDentistReport = false,
}: {
  phase: "patient" | "dentist" | "score";
  compact?: boolean;
  skipDentistReport?: boolean;
}) {
  const steps = skipDentistReport
    ? ([
        { key: "patient", label: "患者面接" },
        { key: "score", label: "スコア" },
      ] as const)
    : ([
        { key: "patient", label: "患者面接" },
        { key: "dentist", label: "歯科医報告" },
        { key: "score", label: "スコア" },
      ] as const);
  const curIdx = steps.findIndex((s) => s.key === phase);
  const circleSize = compact ? 18 : 20;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 5 : 8,
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
      }}
    >
      {steps.map((s, i) => {
        const active = i === curIdx;
        const done = i < curIdx;
        const circleBg = active
          ? "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)"
          : done
            ? "#10B981"
            : "transparent";
        const circleFg = active || done ? "#FFFFFF" : "rgba(255,255,255,0.55)";
        const circleBorder = active
          ? "transparent"
          : done
            ? "#10B981"
            : "rgba(255,255,255,0.3)";
        const labelColor = active ? "#FFB787" : done ? "#6EE7B7" : "rgba(255,255,255,0.55)";
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: compact ? 4 : 6 }}>
            <div
              style={{
                width: circleSize,
                height: circleSize,
                borderRadius: circleSize / 2,
                border: `1.5px solid ${circleBorder}`,
                background: circleBg,
                color: circleFg,
                fontSize: compact ? 9 : 10,
                fontWeight: 800,
                display: "grid",
                placeItems: "center",
                fontFamily: "system-ui",
                boxShadow: active
                  ? "0 0 0 3px rgba(255,138,61,0.18), 0 4px 12px rgba(255,107,26,0.5)"
                  : "none",
              }}
            >
              {done ? "✓" : i + 1}
            </div>
            <span
              style={{
                fontSize: compact ? 10 : 11,
                fontWeight: active ? 800 : 600,
                color: labelColor,
                letterSpacing: 0.3,
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <div
                style={{
                  width: compact ? 10 : 18,
                  height: 1.5,
                  background: done ? "#10B981" : "rgba(255,255,255,0.25)",
                  borderRadius: 1,
                  marginLeft: compact ? 2 : 4,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
